import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Divider,
  Chip,
  Alert,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  PercentIcon,
} from '@mui/material';
import AlertModal from '../../components/AlertModal';
import {
  Person,
  Home,
  AttachMoney,
  Search,
  LocationOn,
  Business,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import { useLocation, useHistory } from 'react-router-dom';
import { customerService } from '../../services/customer';
import { getProjects, getPlotsByProject, createBooking, searchFloorData, searchFlatData, getFlyCommission, getPlotsByProjectSearch } from '../../services/project';
import { getAssociates } from '../../services/associate';
import { globalMasterService } from '../../services/stateAPI';

import { hasWritePermission } from '../../utils/menuFilter';

const APPROVED_STATUS = {
  PENDING: 'pending',
  BOOKED: 'booked',
  ALLOTED: 'alloted',
  CANCELLED: 'cancelled',
};

const RELATION_OPTIONS = [
  'Self',
  'Spouse',
  'Father',
  'Mother',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
  'Friend',
  'Wife',
  'Husband',
  'Other',
];

const validateMobile = (mobile) => {
  if (!mobile) return false;
  const mobileRegex = /^[6-9]\d{9}$/;
  return mobileRegex.test(mobile);
};

const BookPlot = () => {
  const canWritePlotBooking = hasWritePermission('booking.add-booking');
  const location = useLocation();
  const history = useHistory();
  const leadData = location.state?.leadData || null;
  const associateData = location.state?.associateData || null;
  const visitId = location.state?.visitId || null;

  const leadDataProcessedRef = useRef(false);
  const processedLeadIdRef = useRef(null);
  const initialCustomerDataRef = useRef(null);

  const [step, setStep] = useState(1);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });

  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const [applicants, setApplicants] = useState([]); // Additional applicants (co-applicants)

  const [customerData, setCustomerData] = useState({
    customerType: 'existing',
    customerID: '',
    customerId: '',
    customerNo: null,
    name: '',
    fatherHusband: '',
    gender: '',
    maritalStatus: '',
    dob: '',
    anniversaryDate: '',
    mobile: '',
    altMobile: '',
    associateContact: '',
    associateId: '',
    associateName: '',
    address: '',
    pinCode: '',
    city: '',
    state: '',
    // associate: '',
  });

  const [customerDirectory, setCustomerDirectory] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [applicantSearchInputs, setApplicantSearchInputs] = useState([]); // per-applicant search text
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingCustomersList, setLoadingCustomersList] = useState(false);
  const [addApplicantDialogOpen, setAddApplicantDialogOpen] = useState(false);
  const [newApplicantForm, setNewApplicantForm] = useState({
    name: '',
    mobile: '',
    altMobile: '',
    relation: '',
    email: '',
  });
  const [newApplicantErrors, setNewApplicantErrors] = useState({});
  const [creatingApplicantCustomer, setCreatingApplicantCustomer] = useState(false);
  const customerSearchTimeoutRef = useRef(null);
  const customerSelectedRef = useRef(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);

  const [potentialCustomerMatches, setPotentialCustomerMatches] = useState([]);
  const [checkingExistingCustomer, setCheckingExistingCustomer] = useState(false);
  const [showExistingCustomerAlert, setShowExistingCustomerAlert] = useState(false);
  const newCustomerCheckTimeoutRef = useRef(null);

  const [associateDirectory, setAssociateDirectory] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateSearchTimeoutRef = useRef(null);
  const associatesLoadedRef = useRef(false);
  const associateDataSetRef = useRef(false);
  const associateSelectedRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const [loadingMoreAssociates, setLoadingMoreAssociates] = useState(false);

  const [stateSearchInput, setStateSearchInput] = useState('');

  // Commission configuration from selected project
  const [projectCommission, setProjectCommission] = useState(null);
  const [commissionOptions, setCommissionOptions] = useState([]);
  const [loadingCommissionOptions, setLoadingCommissionOptions] = useState(false);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch commission options from API
  const fetchCommissionOptions = useCallback(async () => {
    if (loadingCommissionOptions) return;

    setLoadingCommissionOptions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=commission`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('Commission API response:', result);
      const commissions = Array.isArray(result?.data) ? result.data : [];

      // Extract commission values from the response
      // Filter only active commissions and extract commissionPercentage
      const options = commissions
        .filter((item) => item?.status === true) // Only active commissions
        .map((item) => {
          const value = item?.details?.commissionPercentage ||
            item?.details?.commission ||
            item?.details?.commissionValue ||
            item?.commissionPercentage ||
            item?.commission ||
            item?.value ||
            null;
          return value !== null ? String(value) : null;
        })
        .filter(Boolean)
        .sort((a, b) => parseFloat(a) - parseFloat(b)); // Sort numerically

      console.log('Mapped commission options:', options);
      setCommissionOptions(options);
    } catch (error) {
      console.error('Error fetching commission options:', error);
      setCommissionOptions([]);
    } finally {
      setLoadingCommissionOptions(false);
    }
  }, [loadingCommissionOptions]);


  const [addCityDialog, setAddCityDialog] = useState({ open: false, cityName: '' });

  const [bookingTypes, setBookingTypes] = useState([]);
  const [loadingBookingTypes, setLoadingBookingTypes] = useState(false);

  const [paymentPlans, setPaymentPlans] = useState([]);
  const [loadingPaymentPlans, setLoadingPaymentPlans] = useState(false);

  // Banks from API
  const [banksList, setBanksList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const [statesList, setStatesList] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);

  const [citiesList, setCitiesList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [loadingMaritalStatuses, setLoadingMaritalStatuses] = useState(false);

  const [projectDirectory, setProjectDirectory] = useState([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [plotsList, setPlotsList] = useState([]);
  const [loadingPlots, setLoadingPlots] = useState(false);
  const [buildingsList, setBuildingsList] = useState([]);
  const [floorsList, setFloorsList] = useState([]);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [flatsList, setFlatsList] = useState([]);
  const [loadingFlats, setLoadingFlats] = useState(false);

  const inventoryDirectory = useMemo(() => ({
    'Site A': {
      towers: {
        T1: { units: { 'A101': { area: 1200, bhk: 2, baths: 2 }, 'A102': { area: 1300, bhk: 3, baths: 3 } } },
        T2: { units: { 'B201': { area: 900, bhk: 1, baths: 1 }, 'B202': { area: 1000, bhk: 2, baths: 2 } } },
      },
      sectors: {
        'Sector 1': { plots: { 'P-101': 1000, 'P-102': 1200, 'P-103': 900 } },
        'Sector 2': { plots: { 'P-201': 1100, 'P-202': 1250 } },
      },
    },
    'Site B': {
      towers: {
        T1: { units: { 'C101': { area: 1100, bhk: 2, baths: 2 }, 'C102': { area: 1150, bhk: 2, baths: 2 } } },
      },
      sectors: {
        'Sector 1': { plots: { 'B-101': 950, 'B-102': 1050 } },
      },
    },
    'Site C': {
      towers: {
        T3: { units: { 'D501': { area: 1500, bhk: 3, baths: 3 } } },
      },
      sectors: {
        'Sector 3': { plots: { 'C-301': 1400 } },
      },
    }
  }), []);

  const [plotData, setPlotData] = useState({
    bookingType: 'Plot',
    projectName: '',
    projectId: '',
    rewardPoints: 0,
    site: '',
    sector: '',
    block: '',
    plotNo: '',
    plotName: '',
    plotHeight: '',
    plotWidth: '',
    plotArea: '',
    tower: '',
    floor: '',
    unitNo: '',
    carpetArea: '',
    builtUpArea: '',
    superArea: '',
    bedrooms: '',
    bathrooms: '',
    villaType: '',
    unitSize: '',
    actualPlotAmount: '',
    plotRate: '',
    sellingRate: '',
    discountPlotRate: '',
    discountAmount: '',
    otherCharges: '',
    authorityChargesType: '',
    authorityChargesPerSqft: '',
    totalAuthorityCharges: '',
    includeAuthorityCharges: true,
    companyDiscount: '0',
    companyDiscountPercent: '0',
    discountPerSqFt: '0',
    totalDiscountValue: '0',
    plcPercent: '0',
    otherDiscount: '',
    totalPaid: '',
    netTotal: '',
    dueAmount: '',
    paymentAmount: '',
    bookingAmount: '',
    receivedBy: '',
    givenBy: '',
    cashDepositBy: '',
    narration: '',
    manualReceiptNo: '',
    payoutProceed: false,
    paymentDate: '',
    mode: '',
    refNo: '',
    bankName: '',
    receiverName: '',
    upiId: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    cardLast4: '',
    commissionMethod: '',
    commission: '',
    levelCommission: '',
    associateCommission: '',
    facing: '',
  });

  // Associate commission state
  const [addAssociateCommission, setAddAssociateCommission] = useState(false);
  const [associateCommissionPercent, setAssociateCommissionPercent] = useState(null);
  const [associateCommissionMax, setAssociateCommissionMax] = useState(0);
  const [associateCommissionLoading, setAssociateCommissionLoading] = useState(false);
  const [associateCommissionError, setAssociateCommissionError] = useState('');
  const [baseNetTotal, setBaseNetTotal] = useState(0);

  // Keep base net total in sync while associate commission is not applied
  useEffect(() => {
    if (!addAssociateCommission) {
      const net = Number(plotData.netTotal || 0);
      setBaseNetTotal(isNaN(net) ? 0 : net);
    }
  }, [plotData.netTotal, addAssociateCommission]);

  useEffect(() => {
    // Extract actual customer ID and avoid processing the same lead twice
    const rawCustomerId = leadData?.customerId?._id || leadData?.customerId?.id || leadData?.customerId;
    const leadId = leadData?.id || rawCustomerId || leadData?._id;

    if (!leadData || (leadDataProcessedRef.current && processedLeadIdRef.current === leadId)) {
      return;
    }

    const loadCustomerData = async () => {
      leadDataProcessedRef.current = true;
      processedLeadIdRef.current = leadId;

      // Determine if this should be treated as a new or existing customer
      const shouldShowAsNewCustomer = leadData?.isExistingCustomer === false;
      const customerType = shouldShowAsNewCustomer ? 'new' : 'existing';

      // Case 1: We have a customer ID - fetch full details
      if (rawCustomerId) {
        setLoadingCustomer(true);
        try {
          const customer = await customerService.getCustomers(rawCustomerId);
          if (customer) {
            const customerID = customer.customerNo
              ? `CUS-${customer.customerNo}`
              : (customer._id || customer.id || rawCustomerId);

            const formattedCustomer = {
              id: customer._id || customer.id || rawCustomerId,
              customerId: customer._id || customer.id || rawCustomerId,
              customerNo: customer.customerNo,
              name: customer.name || leadData.name || '',
              mobile: customer.phone || customer.mobile || leadData.phone || '',
              email: customer.email || leadData.email || '',
              displayLabel: `${customer.name || leadData.name || ''} — ${customer.phone || customer.mobile || leadData.phone || ''}`,
              fullData: customer,
            };

            setCustomerDirectory(prev => {
              const exists = prev.some(c => c.id === formattedCustomer.id);
              return exists ? prev : [formattedCustomer, ...prev];
            });

            const updatedCustomerData = {
              customerType: customerType,
              customerID: customerID,
              customerId: customer._id || customer.id || rawCustomerId,
              customerNo: customer.customerNo || null,
              name: customer.name || leadData.name || '',
              mobile: customer.phone || customer.mobile || leadData.phone || '',
              altMobile: customer.whatsapp || customer.altPhone || customer.mobile || customer.phone || leadData.phone || '',
              email: customer.email || leadData.email || '',
              address: customer.otherDetails?.address || customer.address || leadData.location || leadData.address || '',
              city: customer.city || leadData.city || '',
              state: customer.state || leadData.state || '',
              pinCode: customer.pinCode || customer.otherDetails?.pinCode || '',
              fatherHusband: customer.basicDetails?.fatherName || customer.fatherHusband || '',
              gender: customer.basicDetails?.gender || customer.gender || '',
              maritalStatus: customer.personalDetails?.maritalStatus || customer.maritalStatus || '',
              dob: customer.personalDetails?.dob || customer.dob || '',
              anniversaryDate: customer.personalDetails?.anniversary || customer.anniversaryDate || '',
            };

            setCustomerData(prev => ({ ...prev, ...updatedCustomerData }));
          }
        } catch (error) {
          console.error('Failed to load customer data:', error);
        } finally {
          setLoadingCustomer(false);
        }
      }
      // Case 2: No customer ID, but we have name/phone (likely a new lead or direct navigation)
      else if (leadData.name || leadData.phone) {
        setCustomerData(prev => ({
          ...prev,
          customerType: shouldShowAsNewCustomer ? 'new' : 'existing',
          name: leadData.name || '',
          mobile: leadData.phone || '',
          email: leadData.email || '',
          address: leadData.location || leadData.address || '',
          city: leadData.city || '',
          state: leadData.state || '',
        }));
      }

      // Auto-fill associate if provided in leadData and not already set by associateData
      if (leadData.associateId && !associateData && !customerData.associateId) {
        setCustomerData(prev => ({
          ...prev,
          associateId: leadData.associateId,
          associateName: leadData.visitedBy || leadData.associateName || '',
          associateContact: leadData.visitedByPhone || leadData.associatePhone || '',
        }));
      }
    };

    loadCustomerData();
  }, [leadData, associateData]);

  useEffect(() => {
    const loadBookingTypes = async () => {
      setLoadingBookingTypes(true);
      try {
        const types = await globalMasterService.getProjectTypes();
        setBookingTypes(types);

        if (!plotData.bookingType && types.length > 0) {
          setPlotData(prev => ({ ...prev, bookingType: types[0] }));
        }
      } catch (error) {
        console.error('Failed to load booking types:', error);
        setBookingTypes(['Plot', 'Villa', 'Apartment']);
      } finally {
        setLoadingBookingTypes(false);
      }
    };

    loadBookingTypes();
  }, []);

  useEffect(() => {
    const loadPaymentPlans = async () => {
      setLoadingPaymentPlans(true);
      try {
        const plans = await globalMasterService.getPaymentPlans();
        setPaymentPlans(
          plans
            .filter(plan => plan.status === true)
            .map(plan => plan.details.planName)
        );

      } catch (error) {
        console.error('Failed to load payment plans:', error);
        setPaymentPlans([]);
      } finally {
        setLoadingPaymentPlans(false);
      }
    };

    loadPaymentPlans();
  }, []); // Run only once on mount

  useEffect(() => {
    const loadBanks = async () => {
      setLoadingBanks(true);
      try {
        const banks = await globalMasterService.getBanks();
        const mapped = Array.isArray(banks)
          ? banks
            .map((bank) => ({
              id: bank.id || bank._id || '',
              bankName: bank.bankName || bank.details?.bankName || bank.name || '',
            }))
            .filter((b) => b.id && b.bankName)
          : [];

        if (mapped.length > 0) {
          setBanksList(mapped);
        } else {
          setBanksList([
            { id: 'bank-sbi', bankName: 'State Bank Of India' },
            { id: 'bank-dena', bankName: 'Dena Bank' },
            { id: 'bank-yes', bankName: 'Yes Bank' },
            { id: 'bank-iob', bankName: 'Indian Overseas Bank' },
            { id: 'bank-hdfc', bankName: 'HDFC' },
          ]);
        }
      } catch (error) {
        console.error('Failed to load banks:', error);
        setBanksList([
          { id: 'bank-sbi', bankName: 'State Bank Of India' },
          { id: 'bank-dena', bankName: 'Dena Bank' },
          { id: 'bank-yes', bankName: 'Yes Bank' },
          { id: 'bank-iob', bankName: 'Indian Overseas Bank' },
          { id: 'bank-hdfc', bankName: 'HDFC' },
        ]);
      } finally {
        setLoadingBanks(false);
      }
    };

    loadBanks();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const states = await globalMasterService.getStates();
        setStatesList(states);
      } catch (error) {
        console.error('Failed to load states:', error);
        setStatesList(['Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'West Bengal']);
      } finally {
        setLoadingStates(false);
      }
    };

    loadStates();
  }, []);
  useEffect(() => {
    const loadCities = async () => {
      setLoadingCities(true);
      try {
        const cities = await globalMasterService.getCities();
        setCitiesList(cities);
      } catch (error) {
        console.error('Failed to load cities:', error);
        setCitiesList([
          { cityName: 'Varanasi', state: 'UP' },
          { cityName: 'Lucknow', state: 'UP' },
          { cityName: 'Kanpur', state: 'UP' },
          { cityName: 'Nainital', state: 'UK' },
          { cityName: 'Dehradun', state: 'UK' },
        ]);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, []); // Run only once on mount

  useEffect(() => {
    const loadMaritalStatuses = async () => {
      setLoadingMaritalStatuses(true);
      try {
        const statuses = await globalMasterService.getMaritalStatuses();
        setMaritalStatuses(statuses);
      } catch (error) {
        console.error('Failed to load marital statuses:', error);
        setMaritalStatuses([
          { value: 'Single', label: 'Single', id: 'single', source: 'default' },
          { value: 'Married', label: 'Married', id: 'married', source: 'default' },
          { value: 'Other', label: 'Other', id: 'other', source: 'default' },
        ]);
      } finally {
        setLoadingMaritalStatuses(false);
      }
    };

    loadMaritalStatuses();
  }, []);

  const cityOptions = useMemo(() => {
    if (!customerData.state) {
      return citiesList.map(city => city.cityName);
    }
    return citiesList
      .filter(city => city.state.toLowerCase() === customerData.state.toLowerCase())
      .map(city => city.cityName);
  }, [citiesList, customerData.state]);

  const handleOpenAddCityDialog = () => {
    setAddCityDialog({ open: true, cityName: '' });
  };

  const handleCloseAddCityDialog = () => {
    setAddCityDialog({ open: false, cityName: '' });
  };

  const handleAddCity = () => {
    if (addCityDialog.cityName.trim()) {
      const newCity = {
        cityName: addCityDialog.cityName.trim(),
        state: customerData.state || '',
        id: `custom-${Date.now()}`
      };

      setCitiesList(prev => [...prev, newCity]);

      setCustomerData(prev => ({ ...prev, city: addCityDialog.cityName.trim() }));

      handleCloseAddCityDialog();
    }
  };

  useEffect(() => {
    const loadProjects = async () => {
      if (plotData.bookingType) {
        setLoadingProjects(true);
        try {
          const projectType = plotData.bookingType;
          const projects = await getProjects({ projectType });
          setProjectDirectory(projects);
          setPlotData(prev => ({ ...prev, projectName: '', projectId: '', rewardPoints: 0, plotNo: '', plotName: '' }));
          setPlotsList([]);
        } catch (error) {
          console.error('Failed to load projects:', error);
          setProjectDirectory([]);
        } finally {
          setLoadingProjects(false);
        }
      }
    };

    loadProjects();
  }, [plotData.bookingType]);

  const loadCustomersList = useCallback(async (searchTerm = '', page = 1, append = false) => {
    if (append) {
      setLoadingMoreCustomers(true);
    } else {
      setLoadingCustomersList(true);
    }
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        limit: '20',
      });
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${base}/api/customers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const result = await response.json();
      const customers = Array.isArray(result?.data) ? result.data : [];
      const hasMore = customers.length === 20; // If we got 20 items, there might be more

      const formattedCustomers = customers.map(customer => ({
        id: customer._id,
        customerId: customer._id,
        customerNo: customer.customerNo,
        name: customer.name || '',
        mobile: customer.phone || customer.mobile || '',
        email: customer.email || '',
        displayLabel: `${customer.name || ''} — ${customer.phone || customer.mobile || ''}`,
        fullData: customer,
      }));

      if (append) {
        setCustomerDirectory(prev => {
          const existingIds = new Set(prev.map(c => String(c.id || c.customerId || '')));
          const newCustomers = formattedCustomers.filter(c => {
            const idStr = String(c.id || c.customerId || '');
            return !existingIds.has(idStr);
          });
          return [...prev, ...newCustomers];
        });
        setCustomerHasMore(hasMore);
      } else {
        setCustomerDirectory(formattedCustomers);
        setCustomerHasMore(hasMore);
        setCustomerPage(1);
      }
    } catch (error) {
      console.error('Failed to load customers list:', error);
      if (!append) {
        setCustomerDirectory([]);
      }
      setCustomerHasMore(false);
    } finally {
      if (append) {
        setLoadingMoreCustomers(false);
      } else {
        setLoadingCustomersList(false);
      }
    }
  }, []);

  const checkForExistingCustomer = useCallback(async (mobile = '', name = '') => {
    if (!mobile && !name) return;

    setCheckingExistingCustomer(true);
    try {
      let searchTerm = '';
      if (mobile) {
        searchTerm = mobile;
      } else if (name) {
        searchTerm = name;
      }

      const customers = await customerService.getCustomers(null, searchTerm);

      const matches = customers.filter(customer => {
        const customerMobile = customer.phone || customer.mobile || '';
        const customerName = customer.name || '';

        if (mobile && customerMobile === mobile) {
          return true;
        }

        if (name && customerName.toLowerCase().includes(name.toLowerCase())) {
          return true;
        }

        return false;
      });

      setPotentialCustomerMatches(matches.slice(0, 5));
      setShowExistingCustomerAlert(matches.length > 0);
    } catch (error) {
      console.error('Failed to check for existing customers:', error);
      setPotentialCustomerMatches([]);
      setShowExistingCustomerAlert(false);
    } finally {
      setCheckingExistingCustomer(false);
    }
  }, []);

  const loadAssociatesList = useCallback(async (searchTerm = '', page = 1, append = false) => {
    if (loadingAssociates && !append) return;
    if (append) {
      setLoadingMoreAssociates(true);
    } else {
      setLoadingAssociates(true);
    }
    try {
      const params = {
        page: String(page),
        limit: '20',
      };
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await getAssociates(params);
      const associates = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);
      const hasMore = associates.length === 20; // If we got 20 items, there might be more

      const formattedAssociates = associates.map(associate => ({
        id: associate._id,
        associateId: associate.associateId || associate._id,
        name: associate.name || '',
        mobile: associate.phone || associate.mobile || associate.contactNumber || '',
        email: associate.email || '',
        phone: associate.phone || associate.mobile || associate.contactNumber || '',
        commission: associate.commission || '',
        displayLabel: `${associate.name || ''} — ${associate.phone || associate.mobile || associate.contactNumber || ''}`,
        fullData: associate,
      }));

      setAssociateDirectory(prev => {
        if (append) {
          // Append mode: add new associates to existing list
          const existingIds = new Set(prev.map(a => String(a.id || a._id || '')));
          const newAssociates = formattedAssociates.filter(a => {
            const idStr = String(a.id || a._id || '');
            return !existingIds.has(idStr);
          });

          // Preserve associateData if it exists
          const associateDataItem = prev.find(a => {
            if (!associateData) return false;
            const aIdStr = String(a.id || a._id || '');
            const aAssociateIdStr = String(a.associateId || '');
            const dataIdStr = String(associateData.id || associateData._id || '');
            const dataAssociateIdStr = String(associateData.associateId || '');
            return aIdStr === dataIdStr ||
              aAssociateIdStr === dataAssociateIdStr ||
              (a.name === associateData.name && a.mobile === associateData.mobile);
          });

          if (associateDataItem) {
            const otherExisting = prev.filter(a => a !== associateDataItem);
            return [associateDataItem, ...otherExisting, ...newAssociates];
          }

          return [...prev, ...newAssociates];
        } else {
          // Replace mode: replace with new search results
          if (searchTerm) {
            return formattedAssociates;
          }

          const associateDataItem = prev.find(a => {
            if (!associateData) return false;
            const aIdStr = String(a.id || a._id || '');
            const aAssociateIdStr = String(a.associateId || '');
            const dataIdStr = String(associateData.id || associateData._id || '');
            const dataAssociateIdStr = String(associateData.associateId || '');
            return aIdStr === dataIdStr ||
              aAssociateIdStr === dataAssociateIdStr ||
              (a.name === associateData.name && a.mobile === associateData.mobile);
          });

          const existingIds = new Set(prev.map(a => String(a.id || a._id || '')));
          const newAssociates = formattedAssociates.filter(a => {
            const idStr = String(a.id || a._id || '');
            return !existingIds.has(idStr);
          });

          if (associateDataItem) {
            const otherExisting = prev.filter(a => a !== associateDataItem);
            return [associateDataItem, ...otherExisting, ...newAssociates];
          }

          return [...prev, ...newAssociates];
        }
      });

      setAssociateHasMore(hasMore);
      if (!append) {
        setAssociatePage(1);
      }

      if (!searchTerm && !append) {
        associatesLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to load associates list:', error);
      if (!associateData && !append) {
        setAssociateDirectory([]);
      }
      setAssociateHasMore(false);
    } finally {
      if (append) {
        setLoadingMoreAssociates(false);
      } else {
        setLoadingAssociates(false);
      }
    }
  }, [associateData]);

  useEffect(() => {
    if (customerData.customerType === 'existing' && customerDirectory.length === 0 && !loadingCustomersList) {
      setCustomerPage(1);
      setCustomerHasMore(true);
    }
  }, [customerData.customerType, customerDirectory.length, loadingCustomersList, loadCustomersList]);

  useEffect(() => {
    if (customerData.customerType !== 'new') {
      setPotentialCustomerMatches([]);
      setShowExistingCustomerAlert(false);
      setCheckingExistingCustomer(false);
      if (newCustomerCheckTimeoutRef.current) {
        clearTimeout(newCustomerCheckTimeoutRef.current);
      }
    }
  }, [customerData.customerType]);

  useEffect(() => {
    // Skip API call if a customer was just selected
    if (customerSelectedRef.current) {
      customerSelectedRef.current = false;
      return;
    }

    if (customerData.customerType === 'existing') {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }

      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerPage(1);
        setCustomerHasMore(true);
        loadCustomersList(customerSearchInput, 1, false);
      }, 500);

      return () => {
        if (customerSearchTimeoutRef.current) {
          clearTimeout(customerSearchTimeoutRef.current);
        }
      };
    }
  }, [customerSearchInput, customerData.customerType, loadCustomersList]);

  useEffect(() => {
    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
      if (newCustomerCheckTimeoutRef.current) {
        clearTimeout(newCustomerCheckTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (associateData) {
      setAssociateDirectory(prev => {
        const exists = prev.some(a => {
          const aIdStr = String(a.id || a._id || '');
          const aAssociateIdStr = String(a.associateId || '');
          const dataIdStr = String(associateData.id || associateData._id || '');
          const dataAssociateIdStr = String(associateData.associateId || '');

          return aIdStr === dataIdStr ||
            aAssociateIdStr === dataAssociateIdStr ||
            (a.name === associateData.name && a.mobile === associateData.mobile);
        });
        if (!exists) {
          return [associateData, ...prev];
        }
        return prev;
      });
    }
  }, [associateData]);

  useEffect(() => {
    if (!associateData && !associatesLoadedRef.current && !loadingAssociates && associateDirectory.length === 0) {
      setAssociatePage(1);
      setAssociateHasMore(true);
    }
  }, [loadAssociatesList]);

  useEffect(() => {
    // Skip API call if an associate was just selected
    if (associateSelectedRef.current) {
      associateSelectedRef.current = false;
      return;
    }

    if (associateData && !associateSearchInput) {
      return;
    }

    if (!associateSearchInput && associatesLoadedRef.current) {
      return;
    }

    if (associateSearchTimeoutRef.current) {
      clearTimeout(associateSearchTimeoutRef.current);
    }

    associateSearchTimeoutRef.current = setTimeout(() => {
      setAssociatePage(1);
      setAssociateHasMore(true);
      loadAssociatesList(associateSearchInput, 1, false);
    }, 500);

    return () => {
      if (associateSearchTimeoutRef.current) {
        clearTimeout(associateSearchTimeoutRef.current);
      }
    };
  }, [associateSearchInput, associateData, loadAssociatesList]);

  useEffect(() => {
    if (associateData && !associateDataSetRef.current) {

      const associateIdToSet = associateData.id || associateData.associateId || associateData._id || '';
      setCustomerData(prev => {
        if (prev.associateId === associateIdToSet) {
          return prev;
        }
        return {
          ...prev,
          associateContact: associateData.phone || associateData.mobile || '',
          associateId: associateIdToSet,
          associateName: associateData.name || '',
        };
      });
      associateDataSetRef.current = true;
    } else if (leadData?.associateId && associateDirectory.length > 0 && !associateDataSetRef.current && !customerData.associateId) {
      const foundAssociate = associateDirectory.find(
        assoc => {
          const assocIdStr = String(assoc.id || assoc._id || '');
          const assocAssociateIdStr = String(assoc.associateId || '');
          const leadAssociateIdStr = String(leadData.associateId || '');
          return assocIdStr === leadAssociateIdStr ||
            assocAssociateIdStr === leadAssociateIdStr;
        }
      );

      if (foundAssociate) {
        setCustomerData(prev => ({
          ...prev,
          associateContact: foundAssociate.phone || foundAssociate.mobile || '',
          associateId: foundAssociate.id || foundAssociate.associateId || foundAssociate._id || '',
          associateName: foundAssociate.name || '',
        }));
      }
    }
  }, [associateData, leadData?.associateId]);

  const switchToExistingCustomer = useCallback((customer) => {
    const customerID = customer.customerNo
      ? `CUS-${customer.customerNo}`
      : (customer._id || customer.id);

    setCustomerData(prev => ({
      ...prev,
      customerType: 'existing',
      customerID: customerID,
      customerId: customer._id || customer.id,
      customerNo: customer.customerNo || null,
      name: customer.name || '',
      mobile: customer.phone || customer.mobile || '',
      email: customer.email || '',
      address: customer.otherDetails?.address || customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      pinCode: customer.pinCode || customer.otherDetails?.pinCode || '',
      fatherHusband: customer.basicDetails?.fatherName || customer.fatherHusband || '',
      gender: customer.basicDetails?.gender || customer.gender || '',
      maritalStatus: customer.personalDetails?.maritalStatus || customer.maritalStatus || '',
      dob: customer.personalDetails?.dob || customer.dob || '',
      anniversaryDate: customer.personalDetails?.anniversary || customer.anniversaryDate || '',
    }));

    // Add customer to directory so Autocomplete can find it
    const formattedCustomer = {
      id: customer._id || customer.id,
      customerId: customer._id || customer.id,
      customerNo: customer.customerNo,
      name: customer.name || '',
      mobile: customer.phone || customer.mobile || '',
      email: customer.email || '',
      displayLabel: `${customer.name || ''} — ${customer.phone || customer.mobile || ''}`,
      fullData: customer,
    };

    setCustomerDirectory(prev => {
      const exists = prev.some(c => c.id === formattedCustomer.id);
      if (exists) {
        return prev;
      }
      return [formattedCustomer, ...prev];
    });

    // Reset new customer check state
    setPotentialCustomerMatches([]);
    setShowExistingCustomerAlert(false);
  }, []);

  // Handle customer selection and pre-fill form
  const handleCustomerSelect = useCallback(async (selectedCustomer) => {
    if (!selectedCustomer) return;

    try {
      setLoadingCustomer(true);
      // Fetch full customer details using customerId
      const customerIdToFetch = selectedCustomer.customerId || selectedCustomer.id;
      const customer = await customerService.getCustomers(customerIdToFetch);

      if (customer) {
        // Format customer ID as CUS-{customerNo}
        const customerID = customer.customerNo
          ? `CUS-${customer.customerNo}`
          : (customer._id || customer.id);

        setCustomerData(prev => ({
          ...prev,
          customerID: customerID,
          customerId: customer._id || customer.id || customerIdToFetch,
          customerNo: customer.customerNo || null,
          name: customer.name || '',
          mobile: customer.phone || customer.mobile || '',
          altMobile: customer.whatsapp || customer.altPhone || customer.mobile || customer.phone || '',
          email: customer.email || '',
          address: customer.otherDetails?.address || customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          pinCode: customer.pinCode || customer.otherDetails?.pinCode || '',
          fatherHusband: customer.basicDetails?.fatherName || customer.fatherHusband || '',
          gender: customer.basicDetails?.gender || customer.gender || '',
          maritalStatus: customer.personalDetails?.maritalStatus || customer.maritalStatus || '',
          dob: customer.personalDetails?.dob || customer.dob || '',
          anniversaryDate: customer.personalDetails?.anniversary || customer.anniversaryDate || '',
        }));

        if (errors.customerID) {
          setErrors(prev => ({ ...prev, customerID: '' }));
        }
      }
    } catch (error) {
      console.error('Failed to load customer details:', error);
    } finally {
      setLoadingCustomer(false);
    }
  }, [errors]);

  const handleCustomerChange = (e) => {
    let { name, value } = e.target;

    if (name === 'mobile' || name === 'altMobile') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    setCustomerData({ ...customerData, [name]: value });

    // Real-time validation for phone numbers
    if (name === 'mobile' || name === 'altMobile') {
      if (value && value.length < 10) {
        setErrors((prev) => ({ ...prev, [name]: '10 digits required' }));
      } else if (value && !validateMobile(value)) {
        setErrors((prev) => ({ ...prev, [name]: 'Mobile number invalid' }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    } else if (errors[name]) {
      // Clear error for other fields when user starts typing
      setErrors({ ...errors, [name]: '' });
    }
    // If customer type changes, clear initial data ref and reset new customer checks
    if (name === 'customerType') {
      initialCustomerDataRef.current = null;
      setPotentialCustomerMatches([]);
      setShowExistingCustomerAlert(false);
      // Clear existing timeout
      if (newCustomerCheckTimeoutRef.current) {
        clearTimeout(newCustomerCheckTimeoutRef.current);
      }
      return;
    }

    // For new customers, check if entered data matches existing customers
    if (customerData.customerType === 'new' && (name === 'mobile' || name === 'name')) {
      // Clear existing timeout
      if (newCustomerCheckTimeoutRef.current) {
        clearTimeout(newCustomerCheckTimeoutRef.current);
      }

      // Set new timeout for debounced check (500ms delay)
      newCustomerCheckTimeoutRef.current = setTimeout(() => {
        checkForExistingCustomer(
          name === 'mobile' ? value : customerData.mobile,
          name === 'name' ? value : customerData.name
        );
      }, 500);
    }
  };

  // Additional applicants handlers
  const handleAddApplicant = () => {
    setApplicants((prev) => [
      ...prev,
      { name: '', mobile: '', altMobile: '', email: '', address: '', relation: '', customerId: '' }
    ]);
    setApplicantSearchInputs((prev) => [...prev, '']);
    if (errors.applicants) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.applicants;
        return next;
      });
    }
  };

  const handleRemoveApplicant = (index) => {
    setApplicants((prev) => prev.filter((_, i) => i !== index));
    setApplicantSearchInputs((prev) => prev.filter((_, i) => i !== index));
    if (errors.applicants) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.applicants;
        return next;
      });
    }
  };

  const handleApplicantChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'mobile' || field === 'altMobile') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setApplicants((prev) =>
      prev.map((applicant, i) =>
        i === index ? { ...applicant, [field]: processedValue } : applicant
      )
    );
    if (errors.applicants) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.applicants;
        return next;
      });
    }
  };

  const handleOpenAddApplicantDialog = () => {
    // Validate that primary customer is selected and has basic information
    const isNewCustomer = customerData.customerType === 'new';
    if (!customerData.customerId && !isNewCustomer) {
      showAlert('Please select a primary customer first before adding additional applicants.');
      return;
    }

    if (!customerData.name || !customerData.name.trim()) {
      showAlert('Please ensure the primary customer has a name before adding additional applicants.');
      return;
    }

    if (!customerData.mobile || !customerData.mobile.trim()) {
      showAlert('Please ensure the primary customer has a mobile number before adding additional applicants.');
      return;
    }

    if (!customerData.address || !customerData.address.trim()) {
      showAlert('Please ensure the primary customer has an address before adding additional applicants.');
      return;
    }

    setNewApplicantForm({
      name: '',
      mobile: '',
      altMobile: '',
      relation: '',
      email: '',
    });
    setNewApplicantErrors({});
    setAddApplicantDialogOpen(true);
  };

  const handleCloseAddApplicantDialog = () => {
    if (!creatingApplicantCustomer) {
      setAddApplicantDialogOpen(false);
    }
  };

  const validateNewApplicantForm = () => {
    const errors = {};
    if (!newApplicantForm.name.trim()) errors.name = 'Name is required';
    if (!newApplicantForm.mobile.trim()) {
      errors.mobile = 'Mobile is required';
    } else if (!validateMobile(newApplicantForm.mobile.trim())) {
      errors.mobile = 'Mobile number invalid';
    }
    if (newApplicantForm.altMobile && !validateMobile(newApplicantForm.altMobile.trim())) {
      errors.altMobile = 'Alternate number invalid';
    }
    if (newApplicantForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newApplicantForm.email.trim())) {
      errors.email = 'Enter a valid email';
    }
    setNewApplicantErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateApplicantCustomer = async () => {
    if (!validateNewApplicantForm()) return;
    setCreatingApplicantCustomer(true);
    try {
      const currentUserName = getCurrentUserName();
      const payload = {
        name: newApplicantForm.name.trim(),
        profession: '',
        phone: newApplicantForm.mobile.trim(),
        whatsapp: newApplicantForm.mobile.trim(),
        email: newApplicantForm.email?.trim() || '',
        city: '',
        state: '',
        basicDetails: {
          gender: '',
          fatherName: '',
          bloodGroup: '',
        },
        personalDetails: {
          dob: '',
          age: '',
          anniversary: '',
          pan: '',
          aadahr: '',
        },
        otherDetails: {
          address: '',
          altPhone: newApplicantForm.altMobile?.trim() || '',
        },
        associateName: '',
        createdBy: currentUserName,
      };

      const createdCustomer = await customerService.createCustomer(payload);
      const customerId = createdCustomer?._id || createdCustomer?.id || '';
      const displayLabel = `${createdCustomer?.name || newApplicantForm.name} — ${createdCustomer?.phone || newApplicantForm.mobile}`;

      // Add to directory for future selections
      const newDirectoryEntry = {
        id: customerId,
        customerId: customerId,
        name: createdCustomer?.name || newApplicantForm.name,
        mobile: createdCustomer?.phone || createdCustomer?.whatsapp || newApplicantForm.mobile,
        displayLabel,
        fullData: createdCustomer,
      };
      setCustomerDirectory(prev => [newDirectoryEntry, ...prev]);

      // Add as a new applicant row with the provided relation
      setApplicants(prev => [
        ...prev,
        {
          name: createdCustomer?.name || newApplicantForm.name,
          mobile: createdCustomer?.phone || createdCustomer?.whatsapp || newApplicantForm.mobile,
          altMobile: newApplicantForm.altMobile || '',
          address: createdCustomer?.otherDetails?.address || '',
          relation: newApplicantForm.relation || '',
          customerId,
        }
      ]);
      setApplicantSearchInputs(prev => [...prev, newApplicantForm.name]);

      setAddApplicantDialogOpen(false);
      setNewApplicantForm({ name: '', mobile: '', altMobile: '', relation: '', email: '' });
      setNewApplicantErrors({});
    } catch (error) {
      console.error('Failed to create applicant customer:', error);
      showAlert(error?.message || 'Failed to create customer');
    } finally {
      setCreatingApplicantCustomer(false);
    }
  };

  const handleApplicantSearchChange = (index, value) => {
    setApplicantSearchInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    customerSearchTimeoutRef.current = setTimeout(() => {
      setCustomerPage(1);
      setCustomerHasMore(true);
      loadCustomersList(value || '', 1, false);
    }, 500);
  };

  const handleApplicantSelect = (index, customer) => {
    if (!customer) {
      // Clear selection
      setApplicants((prev) =>
        prev.map((a, i) => (i === index ? { ...a, customerId: '', name: '', mobile: '', altMobile: '', email: '' } : a))
      );
      setApplicantSearchInputs((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    const selectedCustomer = customer.fullData || customer;
    const name =
      selectedCustomer.displayLabel?.split(' — ')?.[0] ||
      selectedCustomer.name ||
      customer.name ||
      '';
    const phone =
      selectedCustomer.phone ||
      selectedCustomer.mobile ||
      customer.mobile ||
      selectedCustomer.displayLabel?.split(' — ')?.[1] ||
      '';
    const email =
      selectedCustomer.email ||
      selectedCustomer.contactEmail ||
      customer.email ||
      '';
    const address =
      selectedCustomer.otherDetails?.address ||
      selectedCustomer.address ||
      '';
    const rawRelation =
      selectedCustomer.relation ||
      selectedCustomer.relationship ||
      '';
    // Do not allow "Self" for co-applicants; keep it empty instead
    const relation =
      rawRelation && rawRelation.toLowerCase() === 'self'
        ? ''
        : rawRelation;

    setApplicants((prev) =>
      prev.map((a, i) =>
        i === index
          ? {
            ...a,
            customerId: selectedCustomer._id || selectedCustomer.id || customer.id || customer.customerId || '',
            name: name,
            mobile: phone,
            altMobile: a.altMobile || selectedCustomer.altPhone || selectedCustomer.whatsapp || '',
            email: email,
            address: address,
            relation: relation,
          }
          : a
      )
    );

    // Reflect the chosen name in the input field
    setApplicantSearchInputs((prev) => {
      const next = [...prev];
      next[index] = name;
      return next;
    });

    // Clear errors for this row if any
    if (errors.applicants && Array.isArray(errors.applicants)) {
      const nextErrors = [...errors.applicants];
      nextErrors[index] = {};
      setErrors((prev) => ({ ...prev, applicants: nextErrors }));
    }
  };

  const getCurrentUserName = () => {
    let currentUserName = 'Admin User';
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
      }
    } catch (err) {
      console.error('Error getting user info:', err);
    }
    return currentUserName;
  };

  const handleBookingDate = (event) => {
    // Ensure the date is stored as a Date object.
    if (!event.target.value) {
      setBookingDate('');
      return;
    }
    setBookingDate(new Date(event.target.value)?.toISOString()?.split('T')[0]);
  };


  const recalcAmounts = (state) => {
    const area = state.bookingType === 'Plot'
      ? Number(state.plotArea || 0)
      : Number(state.superArea || state.builtUpArea || state.carpetArea || state.plotArea || 0);
    state.unitSize = String(area || '');
    const rate = Number(state.plotRate || 0);
    const companyDiscountPercent = Number(state.companyDiscountPercent || 0);
    const plcPercent = Number(state.plcPercent || 0);
    const totalAuthorityCharges = (state.includeAuthorityCharges !== false) ? Number(state.totalAuthorityCharges || 0) : 0;

    // Calculate discount per sq ft and total discount value
    if (rate > 0 && companyDiscountPercent > 0 && area > 0) {
      const discountPerSqFt = rate * (companyDiscountPercent / 100);
      const totalDiscountValue = discountPerSqFt * area;

      state.discountPerSqFt = String(Math.round(discountPerSqFt * 100) / 100);
      state.totalDiscountValue = String(Math.round(totalDiscountValue));

      // Calculate discounted price per sq ft
      const discountedPricePerSqFt = rate - discountPerSqFt;

      // Base amount uses discounted price
      const baseAmount = discountedPricePerSqFt * area;

      // PLC amount is calculated on original price (before discount)
      const plcAmount = rate * area * (plcPercent / 100);

      // Net total amount = base amount + PLC amount
      // Base + PLC
      const basePlusPlc = baseAmount + plcAmount;
      // Net total amount = base amount + PLC amount + Authority Charges
      const netTotalAmount = basePlusPlc + totalAuthorityCharges;

      state.actualPlotAmount = String(Math.round(basePlusPlc));
      state.netTotal = String(Math.round(netTotalAmount));
      state.discountAmount = String(Math.round(netTotalAmount)); // Set discountAmount to netTotal for display

      // Calculate company discount amount (total discount for total size)
      state.companyDiscount = String(Math.round(totalDiscountValue));
    } else {
      // Set discountPerSqFt and companyDiscount to "0" if not calculated
      state.discountPerSqFt = "0";
      state.companyDiscount = "0";

      // Use actualPlotAmount if already set (from API), otherwise calculate from area * rate
      let actual = Number(state.actualPlotAmount || 0);
      if (actual === 0 && area > 0 && rate > 0) {
        actual = area * rate;
        state.actualPlotAmount = String(Math.round(actual));
      } else if (actual > 0) {
        // Keep the actualPlotAmount as is (from API)
        state.actualPlotAmount = String(Math.round(actual));
      }

      if (actual > 0) {
        let discount = 0;
        const discountRate = Number(state.discountPlotRate || 0);
        if (discountRate > 0 && rate > 0 && discountRate < rate) {
          discount = (rate - discountRate) * area;
        } else if (Number(state.discountAmount || 0) > 0) {
          discount = Number(state.discountAmount);
        }
        const other = Number(state.otherCharges || 0);
        const compDisc = Number(state.companyDiscount || 0);
        const otherDisc = Number(state.otherDiscount || 0);
        const totalAmount = Math.max(0, Math.round(actual + other - compDisc - otherDisc - discount + totalAuthorityCharges));
        state.netTotal = String(totalAmount);
      }
    }

    const paid = Number(state.totalPaid || 0);
    const netTotal = Number(state.netTotal || 0);
    state.dueAmount = String(Math.max(0, netTotal - paid));

    return state;
  };

  const handlePlotChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    // Prevent negative values for monetary fields
    if (['bookingAmount', 'paymentAmount', 'actualPlotAmount', 'plotRate', 'companyDiscount', 'discountAmount', 'otherCharges', 'associateCommission'].includes(name)) {
      const numValue = Number(processedValue);
      if (!isNaN(numValue) && numValue < 0) {
        processedValue = '0'; // Set to 0 if negative
      }
    }

    const nextState = { ...plotData, [name]: processedValue };
    // Auto-calc plotArea when length/width provided (for Plot type)
    if (plotData.bookingType === 'Plot' && (name === 'plotHeight' || name === 'plotWidth')) {
      const l = name === 'plotHeight' ? Number(processedValue) : Number(nextState.plotHeight || 0);
      const w = name === 'plotWidth' ? Number(processedValue) : Number(nextState.plotWidth || 0);
      if (!Number.isNaN(l) && !Number.isNaN(w) && l > 0 && w > 0) {
        nextState.plotArea = String(l * w);
      }
    }
    // If sector/plot selected for Plot type and inventory has predefined area, auto-fill area
    if (plotData.bookingType === 'Plot' && (name === 'sector' || name === 'plotNo')) {
      const proj = inventoryDirectory[nextState.projectName] || inventoryDirectory[nextState.site];
      const sectorInv = proj?.sectors?.[nextState.sector];
      const areaFromInv = sectorInv?.plots?.[nextState.plotNo];
      if (areaFromInv) {
        nextState.plotArea = String(areaFromInv);
      }
    }
    // If bookingType changed, clear project selection (will be reloaded by useEffect)
    if (name === 'bookingType') {
      nextState.projectName = '';
      nextState.projectId = '';
      nextState.rewardPoints = 0;
      nextState.plotRate = '';
      setBuildingsList([]);
      setPlotsList([]);
      setFloorsList([]);
      setFlatsList([]);
      setProjectCommission(null);
      // Reset associate commission when booking type changes
      setAddAssociateCommission(false);
      setAssociateCommissionPercent(null);
      setAssociateCommissionMax(0);
      setAssociateCommissionError('');
      nextState.associateCommission = '';
    }
    // If building (tower) is selected for Apartment, fetch floors
    if (name === 'tower' && nextState.bookingType === 'Apartment' && value && nextState.projectId) {
      // Clear floor and flat selection when building changes
      nextState.floor = '';
      nextState.unitNo = '';
      setFloorsList([]);
      setFlatsList([]);
      // Fetch floors for the selected building
      setLoadingFloors(true);
      searchFloorData({
        projectId: nextState.projectId,
        buildingId: value
      })
        .then(floors => {
          setFloorsList(floors || []);
        })
        .catch(error => {
          console.error('Failed to load floors:', error);
          setFloorsList([]);
        })
        .finally(() => {
          setLoadingFloors(false);
        });
    }
    // If building is cleared, clear floors and flats
    if (name === 'tower' && !value) {
      nextState.floor = '';
      nextState.unitNo = '';
      setFloorsList([]);
      setFlatsList([]);
    }
    if (name === 'mode') {
      nextState.refNo = '';
      nextState.bankName = '';
      nextState.receiverName = '';
      nextState.upiId = '';
      nextState.accountHolderName = '';
      nextState.accountNumber = '';
      nextState.ifscCode = '';
      nextState.cardLast4 = '';
    }
    // If floor is selected for Apartment, fetch flats
    if (name === 'floor' && nextState.bookingType === 'Apartment' && value && nextState.projectId && nextState.tower) {
      // Clear flat selection when floor changes
      nextState.unitNo = '';
      setFlatsList([]);
      // Find the selected floor to get its ID
      const selectedFloor = floorsList.find(f => {
        const floorValue = f._id || f.id || f.floorNo || f.floorNumber || f.floor || f.name || f.value;
        return floorValue === value;
      });
      const floorId = selectedFloor?._id || selectedFloor?.id || value;
      // Fetch flats for the selected floor
      setLoadingFlats(true);
      searchFlatData({
        projectId: nextState.projectId,
        floorId: floorId,
        buildingId: nextState.tower
      })
        .then(flats => {
          setFlatsList(flats || []);
        })
        .catch(error => {
          console.error('Failed to load flats:', error);
          setFlatsList([]);
        })
        .finally(() => {
          setLoadingFlats(false);
        });
    }
    // If floor is cleared, clear flats
    if (name === 'floor' && !value) {
      nextState.unitNo = '';
      setFlatsList([]);
    }
    // Auto-fill Apartment flat details when unit is selected
    if (name === 'unitNo' && nextState.bookingType === 'Apartment' && value) {
      const selectedFlat = flatsList.find(f => {
        const flatValue = f._id || f.id || f.flatNo || f.flatNumber || f.unitNo || f.name || f.value;
        return flatValue === value;
      });
      if (selectedFlat) {
        nextState.actualPlotAmount = String(selectedFlat.cost || selectedFlat.price || '');
        nextState.plotRate = String(selectedFlat.price || '');
        nextState.plcPercent = String(selectedFlat.plcPercent || '0');
        // Extract BHK number from string like "1 BHK" or use as is
        const bhk = String(selectedFlat.flatType || '').split(' ')[0] || '';
        nextState.bedrooms = bhk;
        // facing is sometimes present in the model
        if (selectedFlat.facing) {
          nextState.facing = selectedFlat.facing;
        }
      }
    }
    // If site acts as project selector, sync projectName
    if (name === 'site') {
      nextState.projectName = nextState.site;
    }
    // Calculate Discounted Amount when Company Discount changes
    if (name === 'companyDiscount') {
      const actualAmount = Number(nextState.actualPlotAmount || 0);
      const companyDisc = Number(value || 0);
      if (actualAmount > 0 && companyDisc > 0) {
        nextState.discountAmount = String(Math.max(0, actualAmount - companyDisc));
      } else {
        nextState.discountAmount = '';
      }
    }
    // Recalculate amounts on area/rate/discount changes
    if ([
      'plotArea', 'plotRate', 'discountPlotRate', 'discountAmount', 'otherCharges', 'companyDiscount', 'companyDiscountPercent', 'plcPercent', 'otherDiscount', 'totalPaid',
      'carpetArea', 'builtUpArea', 'superArea', 'bookingType', 'site', 'projectName', 'includeAuthorityCharges', 'unitNo'
    ].includes(name)) {
      recalcAmounts(nextState);
    }
    setPlotData(nextState);
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!customerData.customerType) {
        newErrors.customerType = 'Please select customer type';
      }
      const isExisting = customerData.customerType === 'existing';
      const isNew = customerData.customerType === 'new';
      const hasSelectedExisting = isExisting && Boolean(customerData.customerId);

      // Common validation for both existing and new customers
      if (!hasSelectedExisting) {
        if (!customerData.name.trim()) {
          newErrors.name = 'Name is required';
        }
        if (!customerData.mobile.trim()) {
          newErrors.mobile = 'Mobile number is required';
        } else if (!validateMobile(customerData.mobile)) {
          newErrors.mobile = 'Mobile number invalid';
        }
      }

      // Validate associate field - mandatory for both types
      if (!customerData.associateId || !customerData.associateName) {
        newErrors.associateId = 'Associate is required';
      }

      // Validate address - always required unless an existing customer is already selected
      if (!hasSelectedExisting) {
        if (!customerData.address.trim()) {
          newErrors.address = 'Address is required';
        }
      }

      // Validate pincode if provided - should be 6 digits
      if (customerData.pincode && customerData.pincode.trim()) {
        if (!/^\d{6}$/.test(customerData.pincode)) {
          newErrors.pincode = 'Pincode must be 6 digits';
        }
      }

      // Validate state and city - only required for existing customers, not for new customers
      if (isExisting && !hasSelectedExisting) {
        // Validate state selection
        if (!customerData.state || !customerData.state.trim()) {
          newErrors.state = 'State is required';
        }

        // Validate city selection
        if (!customerData.city || !customerData.city.trim()) {
          newErrors.city = 'City is required';
        }
      }

      // Additional validation for new customers
      if (isNew) {
        // For new customers, we can add additional validations if needed
        // Currently, name, mobile, and associate are sufficient
      }

      // Additional validation for existing customers (same as new customers for consistency)
      if (isExisting) {
        // Existing customers should also follow the same validation rules
        // Additional validations can be added here if needed in the future
      }

      // Validate additional applicants (co-applicants)
      const filledApplicants = applicants.filter(
        (a) => (a.name || '').trim() !== '' || (a.mobile || '').trim() !== ''
      );
      if (filledApplicants.length > 0) {
        const applicantErrors = [];
        filledApplicants.forEach((a, idx) => {
          const rowErrors = {};
          if (!(a.name || '').trim()) {
            rowErrors.name = 'Name is required for applicant';
          }
          const mobileVal = (a.mobile || '').trim();
          if (!mobileVal) {
            rowErrors.mobile = 'Mobile number is required for applicant';
          } else if (!validateMobile(mobileVal)) {
            rowErrors.mobile = 'Mobile number invalid';
          }
          const altMobileVal = (a.altMobile || '').trim();
          if (altMobileVal && !validateMobile(altMobileVal)) {
            rowErrors.altMobile = 'Alternate number invalid';
          }
          const emailVal = (a.email || '').trim();
          if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
            rowErrors.email = 'Enter a valid email';
          }
          if (Object.keys(rowErrors).length > 0) {
            applicantErrors[idx] = rowErrors;
          }
        });
        if (applicantErrors.length > 0) {
          newErrors.applicants = applicantErrors;
        }
      }
    } else if (stepNumber === 2) {
      // Booking Type is required
      if (!plotData.bookingType) {
        newErrors.bookingType = 'Booking type is required';
      }
      // Project Name is required
      if (!plotData.projectName || !plotData.projectId) {
        newErrors.projectName = 'Project name is required';
      }
      // Plot Number is required for Plot type
      if (plotData.bookingType === 'Plot' && !plotData.plotNo.trim()) {
        newErrors.plotNo = 'Plot number is required';
      }
      // Row House Number is required for RowHouse type
      if (plotData.bookingType === 'RowHouse' && !plotData.plotNo.trim()) {
        newErrors.plotNo = 'Row house number is required';
      }
      // Villa Number is required for Villa type
      if (plotData.bookingType === 'Villa' && !plotData.plotNo.trim()) {
        newErrors.plotNo = 'Villa number is required';
      }
      // For Villa/Apartment, validate unit details
      // For Villa/Apartment, validate unit details
      if (plotData.bookingType === 'Villa' || plotData.bookingType === 'Apartment') {
        // Bathrooms removed from mandatory validation as it's not always in UI/API
      }
      // For Villa, validate villaType
      if (plotData.bookingType === 'Villa') {
        if (!plotData.villaType || !plotData.villaType.trim()) {
          newErrors.villaType = 'Villa type is required';
        }
      }
      // For Apartment, validate bedrooms (BHK)
      if (plotData.bookingType === 'Apartment') {
        if (!plotData.bedrooms) {
          newErrors.bedrooms = 'BHK is required';
        }
      }
      // For Apartment only, validate tower and unitNo (Villa uses plotNo instead)
      if (plotData.bookingType === 'Apartment') {
        if (!plotData.tower) {
          newErrors.tower = 'Tower/Block is required';
        }
        if (!plotData.unitNo) {
          newErrors.unitNo = 'Unit number is required';
        }
      }
      // Pricing fields validation
      if (!plotData.actualPlotAmount) {
        newErrors.actualPlotAmount = 'Actual plot amount is required';
      }
      if (!plotData.plotRate) {
        newErrors.plotRate = 'Plot rate is required';
      }
      // Company discount is optional
      if (!plotData.netTotal && !plotData.discountAmount) {
        newErrors.discountAmount = 'Net Total amount is required';
      }
      // Payment plan validation
      if (!plotData.paymentPlan || !plotData.paymentPlan.trim()) {
        newErrors.paymentPlan = 'Payment plan is required';
      }
      // Payment mode validation + mode-specific required fields
      // Only require payment mode if booking amount is entered
      if (plotData.bookingAmount && Number(plotData.bookingAmount) > 0 && !plotData.mode) {
        newErrors.mode = 'Payment mode is required when booking amount is entered';
      } else if (plotData.mode) {
        const modeRequired = getPaymentModeRequiredFields(plotData.mode);
        modeRequired.forEach((field) => {
          if (!plotData[field] || String(plotData[field]).trim() === '') {
            newErrors[field] = 'Required';
          }
        });
      }
      // bookingAmount and mode are NOT required
      // Remark/narration validation
      // if (!plotData.narration || !plotData.narration.trim()) {
      //   newErrors.narration = 'Remark is required';
      // }
    }

    console.log('Validation result for step', stepNumber, ':', {
      plotData: {
        bookingAmount: plotData.bookingAmount,
        mode: plotData.mode,
        paymentPlan: plotData.paymentPlan,
        netTotal: plotData.netTotal,
        discountAmount: plotData.discountAmount,
        plotRate: plotData.plotRate,
        companyDiscount: plotData.companyDiscount
      },
      errors: newErrors,
      hasErrors: Object.keys(newErrors).length > 0
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPaymentModeRequiredFields = (mode) => {
    switch (mode) {
      // case 'cash':
      //   return ['receiverName'];
      // case 'upi':
      //   return ['receiverName', 'upiId'];
      // case 'bankTransfer':
      // case 'dd':
      //   return ['accountHolderName', 'accountNumber', 'ifscCode', 'bankName'];
      // case 'cheque':
      //   return ['receiverName', 'accountHolderName', 'accountNumber', 'ifscCode', 'bankName', 'refNo'];
      // case 'card':
      //   return ['receiverName', 'cardLast4', 'bankName'];
      default:
        return [];
    }
  };

  const renderPaymentModeFields = () => {
    const commonProps = { fullWidth: true, variant: 'outlined' };
    switch (plotData.mode) {
      case 'Cash':
        return (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              {...commonProps}
              label="Receiver Name"
              name="receiverName"
              value={plotData.receiverName}
              onChange={handlePlotChange}
              error={!!errors.receiverName}
              helperText={errors.receiverName}
            />
          </Grid>
        );
      case 'Upi':
        return (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Receiver Name"
                name="receiverName"
                value={plotData.receiverName}
                onChange={handlePlotChange}
                error={!!errors.receiverName}
                helperText={errors.receiverName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="UPI ID"
                name="upiId"
                value={plotData.upiId}
                onChange={handlePlotChange}
                error={!!errors.upiId}
                helperText={errors.upiId}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.bankName}>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  label="Bank Name"
                  name="bankName"
                  value={plotData.bankName || ''}
                  onChange={handlePlotChange}
                  disabled={loadingBanks}
                >
                  <MenuItem value="">
                    {loadingBanks ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.bankName}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bankName && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.bankName}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </>
        );
      case 'RTGS/NEFT':
      case 'dd':
        return (
          <>
            {/* <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Account Holder Name"
                name="accountHolderName"
                value={plotData.accountHolderName}
                onChange={handlePlotChange}
                error={!!errors.accountHolderName}
                helperText={errors.accountHolderName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Account Number"
                name="accountNumber"
                value={plotData.accountNumber}
                onChange={handlePlotChange}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="IFSC Code"
                name="ifscCode"
                value={plotData.ifscCode}
                onChange={handlePlotChange}
                error={!!errors.ifscCode}
                helperText={errors.ifscCode}
              />
            </Grid> */}

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.bankName}>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  label="Bank Name"
                  name="bankName"
                  value={plotData.bankName || ''}
                  onChange={handlePlotChange}
                  disabled={loadingBanks}
                >
                  <MenuItem value="">
                    {loadingBanks ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.bankName}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bankName && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.bankName}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </>
        );
      case 'Cheque':
        return (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Receiver Name"
                name="receiverName"
                value={plotData.receiverName}
                onChange={handlePlotChange}
                error={!!errors.receiverName}
                helperText={errors.receiverName}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.bankName}>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  label="Bank Name"
                  name="bankName"
                  value={plotData.bankName || ''}
                  onChange={handlePlotChange}
                  disabled={loadingBanks}
                >
                  <MenuItem value="">
                    {loadingBanks ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.bankName}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bankName && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.bankName}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Account Holder Name"
                name="accountHolderName"
                value={plotData.accountHolderName}
                onChange={handlePlotChange}
                error={!!errors.accountHolderName}
                helperText={errors.accountHolderName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Account Number"
                name="accountNumber"
                value={plotData.accountNumber}
                onChange={handlePlotChange}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="IFSC Code"
                name="ifscCode"
                value={plotData.ifscCode}
                onChange={handlePlotChange}
                error={!!errors.ifscCode}
                helperText={errors.ifscCode}
              />
            </Grid> */}
          </>
        );
      case 'card':
        return (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Card Last 4 Digits"
                name="cardLast4"
                inputProps={{ maxLength: 4 }}
                value={plotData.cardLast4}
                onChange={handlePlotChange}
                error={!!errors.cardLast4}
                helperText={errors.cardLast4}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                {...commonProps}
                label="Card Holder Name"
                name="receiverName"
                value={plotData.receiverName}
                onChange={handlePlotChange}
                error={!!errors.receiverName}
                helperText={errors.receiverName}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={!!errors.bankName}>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  label="Bank Name"
                  name="bankName"
                  value={plotData.bankName || ''}
                  onChange={handlePlotChange}
                  disabled={loadingBanks}
                >
                  <MenuItem value="">
                    {loadingBanks ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.bankName}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bankName && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.bankName}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </>
        );
      default:
        return <>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth error={!!errors.bankName}>
              <InputLabel>Bank Name</InputLabel>
              <Select
                label="Bank Name"
                name="bankName"
                value={plotData.bankName || ''}
                onChange={handlePlotChange}
                disabled={loadingBanks}
              >
                <MenuItem value="">
                  {loadingBanks ? 'Loading...' : 'Select Bank'}
                </MenuItem>
                {banksList.map((bank) => (
                  <MenuItem key={bank.id} value={bank.bankName}>
                    {bank.bankName}
                  </MenuItem>
                ))}
              </Select>
              {errors.bankName && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.bankName}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </>;
    }
  };

  // Function to check if customer data has changed
  const hasCustomerDataChanged = () => {
    if (!initialCustomerDataRef.current) {
      return false;
    }

    const initial = initialCustomerDataRef.current;
    const current = customerData;

    // Compare relevant fields that can be edited
    const fieldsToCompare = [
      'name', 'mobile', 'email', 'address', 'city', 'state', 'pincode',
      'fatherHusband', 'gender', 'maritalStatus', 'dob', 'anniversaryDate',
      'associateId', 'associateName', 'associateContact'
    ];

    return fieldsToCompare.some(field => {
      const initialValue = initial[field] || '';
      const currentValue = current[field] || '';
      return initialValue.trim() !== currentValue.trim();
    });
  };

  const handleNext = async () => {
    if (!validateStep(step)) {
      return;
    }

    // If it's a new customer with existing customerId, check if data has changed and update if needed
    if (step === 1 && customerData.customerType === 'new' && customerData.customerId) {
      if (hasCustomerDataChanged()) {
        setUpdatingCustomer(true);
        try {
          // Get current user info for updatedBy
          let currentUserName = 'Yes';
          try {
            const user = localStorage.getItem('user');
            if (user) {
              const userData = JSON.parse(user);
              currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Yes';
            }
          } catch (err) {
            console.error('Error getting user info:', err);
          }

          // Build customer update payload
          const updatePayload = {
            name: customerData.name.trim(),
            profession: '', // Not in form, can be added later
            phone: customerData.mobile.trim(),
            whatsapp: customerData.mobile.trim(),
            email: customerData.email || '',
            city: customerData.city || '',
            state: customerData.state || '',
            basicDetails: {
              gender: customerData.gender || '',
              fatherName: customerData.fatherHusband || '',
              bloodGroup: '', // Not in form
            },
            personalDetails: {
              dob: customerData.dob || '',
              age: '', // Not calculated
              anniversary: customerData.anniversaryDate || '',
              pan: '', // Not in form
              aadahr: '', // Not in form
            },
            otherDetails: {
              address: customerData.address || '',
              createdDetail: '', // Not in form
              remark: '', // Not in form
              salaryRange: '', // Not in form
            },
            associateName: customerData.associateName || '',
            updatedBy: currentUserName,
          };

          // Update the customer
          await customerService.updateCustomer(customerData.customerId, updatePayload);

          // Update initial data ref to reflect the new state
          initialCustomerDataRef.current = { ...customerData };

          // Proceed to next step
          setStep(step + 1);
        } catch (error) {
          console.error('Failed to update customer:', error);
          showAlert(`Failed to update customer: ${error.message}`);
          return; // Don't proceed if update fails
        } finally {
          setUpdatingCustomer(false);
        }
      } else {
        // No changes, just proceed
        setStep(step + 1);
      }
    }
    // If it's a new customer and no customerId exists (from leadData or already set), create the customer first
    else if (step === 1 && customerData.customerType === 'new' && !customerData.customerId && !leadData?.customerId) {
      setCreatingCustomer(true);
      try {
        // Get current user info for createdBy
        let currentUserName = 'Yes';
        try {
          const user = localStorage.getItem('user');
          if (user) {
            const userData = JSON.parse(user);
            currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Yes';
          }
        } catch (err) {
          console.error('Error getting user info:', err);
        }

        // Build customer payload
        const customerPayload = {
          name: customerData.name.trim(),
          profession: '', // Not in form, can be added later
          phone: customerData.mobile.trim(),
          whatsapp: customerData.mobile.trim(),
          email: '', // Not in form, can be added later
          city: customerData.city || '',
          state: customerData.state || '',
          basicDetails: {
            gender: customerData.gender || '',
            fatherName: customerData.fatherHusband || '',
            bloodGroup: '', // Not in form
          },
          personalDetails: {
            dob: customerData.dob || '',
            age: '', // Not calculated
            anniversary: customerData.anniversaryDate || '',
            pan: '', // Not in form
            aadahr: '', // Not in form
          },
          otherDetails: {
            address: customerData.address || '',
            createdDetail: '', // Not in form
            remark: '', // Not in form
            salaryRange: '', // Not in form
          },
          associateName: customerData.associateName || '',
          createdBy: currentUserName,
        };

        // Create the customer
        const createdCustomer = await customerService.createCustomer(customerPayload);

        if (createdCustomer) {
          // Format customer ID as CUS-{customerNo}
          const customerID = createdCustomer.customerNo
            ? `CUS-${createdCustomer.customerNo}`
            : (createdCustomer._id || createdCustomer.id);

          // Store customer data similar to existing customer flow
          setCustomerData(prev => ({
            ...prev,
            customerID: customerID,
            customerId: createdCustomer._id || createdCustomer.id || '',
            customerNo: createdCustomer.customerNo || null,
          }));

          // Proceed to next step
          setStep(step + 1);
        }
      } catch (error) {
        console.error('Failed to create customer:', error);
        showAlert(`Failed to create customer: ${error.message}`);
      } finally {
        setCreatingCustomer(false);
      }
    } else {
      // For existing customers, customers with existing customerId (no changes), or other steps, just proceed
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) {
      return;
    }

    setSubmitting(true);
    try {
      // Get current user info
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Use stored customer ID and customer number
      const customerId = customerData.customerId || customerData.customerID || null;
      const customerNo = customerData.customerNo || null;

      // Get selected plot or flat details
      let facing = '';
      if (plotData.bookingType === 'Apartment') {
        const selectedFlat = flatsList.find(f => {
          const flatValue = f._id || f.id || f.flatNo || f.flatNumber || f.unitNo || f.name || f.value;
          return flatValue === plotData.unitNo;
        });
        facing = selectedFlat?.facing || plotData.facing || '';
      } else {
        const selectedPlot = Array.isArray(plotsList) ? plotsList.find(p => p._id === (plotData.plotNo || plotData.inventoryId)) : null;
        facing = selectedPlot?.facing || '';
      }

      // Normalize additional applicants
      const normalizedApplicants = applicants
        .filter(a => (a.name || '').trim() !== '' || (a.mobile || '').trim() !== '')
        .map(a => ({
          customerId: (a.customerId || '').trim() || undefined,
          name: (a.name || '').trim(),
          mobile: (a.mobile || '').trim(),
          alternateMobile: (a.altMobile || '').trim() || undefined,
          relation: (a.relation || '').trim() || undefined,
          email: (a.email || '').trim() || undefined,
        }));

      // Build payload
      const payload = {
        customerId: customerId || null,
        customerNo: customerNo || null,
        customer: {
          name: customerData.name || '',
          phone: customerData.mobile || '',
          altPhone: customerData.altMobile || customerData.mobile || '',
          address: customerData.address || '',
          pan: '', // Not in form, can be added later
          gender: customerData.gender || '',
          city: customerData.city || '',
          state: customerData.state || '',
        },
        particular: {
          projectId: plotData.projectId || '',
          projectName: plotData.projectName || '',
          inventoryType: plotData.bookingType || 'Plot',
          inventoryId: plotData.bookingType === 'Apartment' ? plotData.unitNo : (plotData.plotNo || ''),
          inventoryDetail: plotData.bookingType === 'Apartment' ? (() => {
            const f = flatsList.find(f => (f._id || f.id || f.flatNo || f.flatNumber || f.unitNo || f.name || f.value) === plotData.unitNo);
            if (!f) return plotData.unitNo || '';
            const num = f.flatNumber || f.flatNo || f.unitNo || '';
            const type = f.flatType ? ` (${f.flatType})` : '';
            return `Unit ${num}${type}`;
          })() : (plotData.plotName || plotData.plotNo || ''),
          areaSize: plotData.plotArea ? `${plotData.plotArea} sqft` : '',
          facing: facing,
          commissionMethod: projectCommission?.method || 'project',
          ...(projectCommission?.percentage || projectCommission?.value ? {
            commission: projectCommission?.percentage || projectCommission?.value
          } : {}),
        },
        associate: customerData.associateId ? {
          associateId: customerData.associateId,
          name: customerData.associateName || '',
          phone: customerData.associateContact || '',
          commission: customerData.associateCommission || '',
        } : {
          associateId: '',
          name: '',
          phone: '',
          commission: '',
        },
        bookingDate: bookingDate ? bookingDate : new Date().toISOString().split('T')[0],
        approvedStatus: APPROVED_STATUS.PENDING,
        propertyAmount: Number(plotData.actualPlotAmount || 0) + Number(plotData.totalAuthorityCharges || 0),
        bookingAmount: Number(plotData.bookingAmount || 0),
        // netTotal: Number(plotData.netTotal || 0),
        mode: plotData.mode || '',
        refNo: plotData.refNo || '',
        bankName: plotData.bankName || '',
        receiverName: plotData.receiverName || '',
        upiId: plotData.upiId || '',
        accountHolderName: plotData.accountHolderName || '',
        accountNumber: plotData.accountNumber || '',
        ifscCode: plotData.ifscCode || '',
        cardLast4: plotData.cardLast4 || '',
        discount: Number(plotData.companyDiscount || 0),
        totalReceived: Number(plotData.bookingAmount || 0),
        paymentPlan: plotData.paymentPlan || '',
        remark: plotData.narration || '',
        companyDiscount: Number(plotData.companyDiscount) || 0,
        isAuthorityCharges: plotData.includeAuthorityCharges !== false,
        authorityChargeName: plotData.authorityChargesType || '',
        totalAuthorityCharge: Number(plotData.totalAuthorityCharges || 0),
        associateCommission: Number(plotData.associateCommission || 0),
        commission: projectCommission?.percentage || '5', // Use project commission or default to 5
        commissionMethod: projectCommission?.method || 'project', // Use project method or default to project
        commissionValue: projectCommission?.method === 'project' ? projectCommission?.percentage : projectCommission?.value || '5', // Use appropriate commission value
        createdBy: currentUserName,
        updatedBy: currentUserName,
        ...(normalizedApplicants.length > 0 ? { additionalCustomers: normalizedApplicants } : {}),
      };

      // Add visitId to payload if it exists (from SiteVisit page)
      if (visitId) {
        payload.visitId = visitId;
      }

      const response = await createBooking(payload);
      console.log('Booking created successfully:', response);
      showAlert('Booking created successfully!');

      // Ensure additionalCustomers are present in the booking data passed to the details page
      const bookingDataToPass = { ...response };
      // If backend didn't echo additionalCustomers back, attach from payload so UI can show them immediately
      if ((!bookingDataToPass.additionalCustomers || bookingDataToPass.additionalCustomers.length === 0) && Array.isArray(payload.additionalCustomers) && payload.additionalCustomers.length > 0) {
        bookingDataToPass.additionalCustomers = payload.additionalCustomers;
        // Also attach to _original for consistency with view components
        bookingDataToPass._original = bookingDataToPass._original || {};
        bookingDataToPass._original.additionalCustomers = payload.additionalCustomers;
      }

      // Redirect to booking details page
      const bookingId = bookingDataToPass?._id || bookingDataToPass?.id || bookingDataToPass?.data?._id || bookingDataToPass?.data?.id;
      history.push({
        pathname: '/app/booking/BookingDetail',
        state: { bookingId: bookingId, bookingData: bookingDataToPass }
      });
    } catch (error) {
      console.error('Error creating booking:', error);
      showAlert(`Failed to create booking: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    'Customer Information',
    'Booking Information',
  ];

  return (
    <Box sx={{
      p: { xs: 1, sm: 3 },
      backgroundColor: '#fafafa'
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5, fontSize: { xs: '1rem', md: '1.125rem' } }}>
            Booking System
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Book with comprehensive customer and booking information
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ py: 3 }}>
            <Stepper activeStep={step - 1} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '14px',
                        fontWeight: 600,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>

            {/* Customer Information Section */}
            {step === 1 && (
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  {/*}  <Box display="flex" alignItems="center" gap={2} mb={3}>
                 <Business sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                  Customer Information
                </Typography> *
              </Box> */}

                  {/* Customer Type Selection */}
                  <Box mb={4}>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
                      Customer Type
                    </FormLabel>
                    <Box display="flex" alignItems="center" gap={2}>
                      <RadioGroup
                        row
                        name="customerType"
                        value={customerData.customerType}
                        onChange={handleCustomerChange}
                        sx={{ gap: 3 }}
                      >
                        <FormControlLabel
                          value="existing"
                          control={<Radio />}
                          label="Existing Customer"
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                        />
                        <FormControlLabel
                          value="new"
                          control={<Radio />}
                          label="New Customer "
                          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                        />
                      </RadioGroup>
                      {loadingCustomer && (
                        <CircularProgress size={20} sx={{ ml: 2 }} />
                      )}
                    </Box>
                    {errors.customerType && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.customerType}
                      </Typography>
                    )}
                  </Box>

                  {/* Alert for new customer simplified requirements */}
                  {customerData.customerType === 'new' && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>New Customer:</strong> Only Name, Mobile Number, and Address are required.
                        State and City are optional.
                      </Typography>
                    </Alert>
                  )}

                  {/* Alert for potential existing customer matches when creating new customer */}
                  {customerData.customerType === 'new' && showExistingCustomerAlert && (
                    <Alert severity="warning" sx={{ mt: 2, mb: 2 }} action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setShowExistingCustomerAlert(false);
                          setPotentialCustomerMatches([]);
                        }}
                      >
                        Dismiss
                      </Button>
                    }>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Potential existing customer match found!
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        The entered details match an existing customer. Please select from the list below or continue with new customer creation.
                      </Typography>
                      {potentialCustomerMatches.map((match, index) => (
                        <Box key={match._id || index} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {match.name} — {match.phone || match.mobile}
                            {match.customerNo && ` (CUS-${match.customerNo})`}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, mr: 1 }}
                            onClick={() => switchToExistingCustomer(match)}
                          >
                            Use Existing Customer
                          </Button>
                        </Box>
                      ))}
                    </Alert>
                  )}

                  {/* Customer ID Search with Autocomplete */}
                  {customerData.customerType === 'existing' && (
                    <Box mb={4}>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <Autocomplete
                          fullWidth
                          sx={{ flexGrow: 1, maxWidth: '400px' }}
                          options={customerDirectory}
                          getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id ||
                            option.customerId === value.customerId ||
                            (option.name === value.name && option.mobile === value.mobile)
                          }
                          loading={loadingCustomersList || loadingMoreCustomers}
                          inputValue={customerSearchInput}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                              // User is typing - update search input
                              setCustomerSearchInput(value);
                            } else if (reason === 'reset' && value) {
                              // User selected an option - show the value but skip API call
                              customerSelectedRef.current = true;
                              setCustomerSearchInput(value);
                            } else if (reason === 'clear') {
                              // User cleared the input
                              setCustomerSearchInput('');
                            }
                          }}
                          value={customerDirectory.find(
                            c => c.id === customerData.customerId ||
                              c.customerId === customerData.customerId ||
                              (customerData.name && c.name === customerData.name && c.mobile === customerData.mobile)
                          ) || (customerData.customerId ? {
                            id: customerData.customerId,
                            customerId: customerData.customerId,
                            customerNo: customerData.customerNo,
                            name: customerData.name,
                            mobile: customerData.mobile,
                            displayLabel: `${customerData.name || ''} — ${customerData.mobile || ''}`
                          } : null)}
                          onChange={(_, value) => {
                            handleCustomerSelect(value);
                          }}
                          ListboxProps={{
                            onScroll: (event) => {
                              const listboxNode = event.currentTarget;
                              if (
                                listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                                customerHasMore &&
                                !loadingMoreCustomers &&
                                !loadingCustomersList
                              ) {
                                const nextPage = customerPage + 1;
                                setCustomerPage(nextPage);
                                loadCustomersList(customerSearchInput, nextPage, true);
                              }
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Customer (Name / Mobile)"
                              error={!!errors.customerID}
                              helperText={errors.customerID}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Search color="action" />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <>
                                    {loadingCustomersList || loadingMoreCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                        <Box sx={{ width: 220 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Customer ID
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {customerData.customerID || 'Select a customer to see ID'}
                          </Typography>
                        </Box>
                      </Box>

                    </Box>
                  )}


                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2', mt: 2 }}>
                    Associate Information
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        fullWidth
                        options={associateDirectory}
                        getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          const optionIdStr = String(option.id || option._id || '');
                          const optionAssociateIdStr = String(option.associateId || '');
                          const valueIdStr = String(value.id || value._id || '');
                          const valueAssociateIdStr = String(value.associateId || '');

                          return optionIdStr === valueIdStr ||
                            optionAssociateIdStr === valueAssociateIdStr ||
                            (option.name === value.name && option.mobile === value.mobile);
                        }}
                        loading={loadingAssociates || loadingMoreAssociates}
                        inputValue={associateSearchInput}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            // User is typing - update search input
                            setAssociateSearchInput(value);
                          } else if (reason === 'reset' && value) {
                            // User selected an option - show the value but skip API call
                            associateSelectedRef.current = true;
                            setAssociateSearchInput(value);
                          } else if (reason === 'clear') {
                            // User cleared the input
                            setAssociateSearchInput('');
                          }
                        }}
                        onOpen={() => {
                          if (!associatesLoadedRef.current && !loadingAssociates) {
                            if (associateData) {
                              const hasAssociateData = associateDirectory.some(a => {
                                const aIdStr = String(a.id || a._id || '');
                                const aAssociateIdStr = String(a.associateId || '');
                                const dataIdStr = String(associateData.id || associateData._id || '');
                                const dataAssociateIdStr = String(associateData.associateId || '');
                                return aIdStr === dataIdStr ||
                                  aAssociateIdStr === dataAssociateIdStr ||
                                  (a.name === associateData.name && a.mobile === associateData.mobile);
                              });

                              if (!hasAssociateData) {
                                setAssociatePage(1);
                                setAssociateHasMore(true);
                                loadAssociatesList('', 1, false);
                              }
                            } else {
                              setAssociatePage(1);
                              setAssociateHasMore(true);
                              loadAssociatesList('', 1, false);
                            }
                          }
                        }}
                        ListboxProps={{
                          onScroll: (event) => {
                            const listboxNode = event.currentTarget;
                            if (
                              listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                              associateHasMore &&
                              !loadingMoreAssociates &&
                              !loadingAssociates
                            ) {
                              const nextPage = associatePage + 1;
                              setAssociatePage(nextPage);
                              loadAssociatesList(associateSearchInput, nextPage, true);
                            }
                          },
                        }}
                        value={(() => {
                          const foundInDirectory = associateDirectory.find(
                            assoc => {
                              const assocIdStr = String(assoc.id || assoc._id || '');
                              const assocAssociateIdStr = String(assoc.associateId || '');
                              const customerAssociateIdStr = String(customerData.associateId || '');

                              return assocIdStr === customerAssociateIdStr ||
                                assocAssociateIdStr === customerAssociateIdStr ||
                                (customerData.associateName && assoc.name === customerData.associateName &&
                                  (assocIdStr === customerAssociateIdStr || assocAssociateIdStr === customerAssociateIdStr));
                            }
                          );

                          if (foundInDirectory) {
                            return foundInDirectory;
                          }

                          if (associateData && customerData.associateId) {
                            const dataIdStr = String(associateData.id || associateData._id || '');
                            const dataAssociateIdStr = String(associateData.associateId || '');
                            const customerAssociateIdStr = String(customerData.associateId || '');

                            if (dataIdStr === customerAssociateIdStr ||
                              dataAssociateIdStr === customerAssociateIdStr ||
                              (customerData.associateName === associateData.name &&
                                (dataIdStr === customerAssociateIdStr || dataAssociateIdStr === customerAssociateIdStr))) {
                              return associateData;
                            }
                          }

                          return null;
                        })()}
                        onChange={(_, value) => {
                          if (value) {
                            setCustomerData(prev => ({
                              ...prev,
                              associateContact: value.phone || value.mobile || '',
                              associateId: value.id || value.associateId || value._id || '',
                              associateName: value.name || '',
                              associateCommission: value.commission || '',
                            }));
                            if (errors.associateId) {
                              setErrors(prev => ({ ...prev, associateId: '' }));
                            }
                          } else {
                            setCustomerData(prev => ({
                              ...prev,
                              associateContact: '',
                              associateId: '',
                              associateName: '',
                              associateCommission: '',
                            }));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Associate (Name / Mobile)"
                            required
                            error={!!errors.associateId}
                            helperText={errors.associateId}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search color="action" />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {loadingAssociates || loadingMoreAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {customerData.associateId && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Associate Phone"
                          value={customerData.associateContact || ''}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              backgroundColor: "#f5f5f5",
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                            },
                          }}
                        />
                      </Grid>
                    )}

                    {/* Associate Commission - only show when associate is selected */}
                    {customerData.associateId && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Associate Commission"
                          value={customerData.associateCommission ? `${customerData.associateCommission}%` : ''}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              backgroundColor: "#f5f5f5",
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                            },
                          }}
                        />
                      </Grid>
                    )}

                  </Grid>

                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#1976d2', mt: 0.5 }}>
                        Customer Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Full Name"
                        name="name"
                        fullWidth
                        value={customerData.name}
                        onChange={handleCustomerChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        variant="outlined"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Father/Husband Name"
                        name="fatherHusband"
                        fullWidth
                        value={customerData.fatherHusband}
                        onChange={handleCustomerChange}
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Select Gender</InputLabel>
                        <Select
                          name="gender"
                          value={customerData.gender}
                          onChange={handleCustomerChange}
                          label="Select Gender"
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Marital Status</InputLabel>
                        <Select
                          name="maritalStatus"
                          value={customerData.maritalStatus}
                          onChange={handleCustomerChange}
                          label="Marital Status"
                          disabled={loadingMaritalStatuses}
                        >
                          <MenuItem value="" disabled>
                            {loadingMaritalStatuses ? 'Loading...' : 'Select Marital Status'}
                          </MenuItem>
                          {maritalStatuses.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {customerData.maritalStatus === 'Married' && (
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Date of Anniversary"
                          name="anniversaryDate"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          value={customerData.anniversaryDate}
                          onChange={handleCustomerChange}
                          variant="outlined"
                          inputProps={{
                            max: new Date().toISOString().split('T')[0],
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Date of Birth"
                        name="dob"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={customerData.dob}
                        onChange={handleCustomerChange}
                        variant="outlined"
                        inputProps={{
                          max: new Date().toISOString().split('T')[0],
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Mobile Number"
                        name="mobile"
                        fullWidth
                        value={customerData.mobile}
                        onChange={handleCustomerChange}
                        error={!!errors.mobile}
                        helperText={errors.mobile || '10 digits (starts with 6-9)'}
                        variant="outlined"
                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        InputProps={{
                          endAdornment: checkingExistingCustomer ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null,
                        }}
                        required
                      />
                    </Grid>





                    <Grid item xs={12}>
                      <TextField
                        label="Address"
                        name="address"
                        fullWidth
                        multiline
                        rows={2}
                        value={customerData.address}
                        onChange={handleCustomerChange}
                        variant="outlined"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        fullWidth
                        freeSolo
                        options={statesList}
                        loading={loadingStates}
                        inputValue={stateSearchInput}
                        onInputChange={(_, value) => setStateSearchInput(value)}
                        value={customerData.state || ''}
                        onChange={(_, value) => setCustomerData(prev => ({ ...prev, state: value || '' }))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={customerData.customerType === 'new' ? "State (Optional)" : "State"}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        select
                        fullWidth
                        label={customerData.customerType === 'new' ? "City (Optional)" : "City"}
                        value={customerData.city || ''}
                        onChange={(e) => {
                          if (e.target.value === "__add_city__") {
                            handleOpenAddCityDialog();
                          } else {
                            setCustomerData(prev => ({ ...prev, city: e.target.value || '' }));
                          }
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        disabled={!customerData.state || customerData.state.trim() === ''}
                        helperText={!customerData.state ? "Please select a state first" : ""}
                        SelectProps={{
                          endAdornment: loadingCities ? (
                            <CircularProgress size={20} style={{ marginRight: 40 }} />
                          ) : null,
                        }}
                      >
                        <MenuItem value="__add_city__" sx={{ color: "#1976d2", fontWeight: 500 }}>
                          ➕ Add New City
                        </MenuItem>
                        {cityOptions.map((city) => (
                          <MenuItem key={city} value={city}>
                            {city}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Pincode"
                        value={customerData.pinCode}
                        onChange={(e) => {
                          const value = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                          setCustomerData(prev => ({ ...prev, pinCode: value }));
                          if (errors.pinCode) setErrors(prev => ({ ...prev, pinCode: '' }));
                        }}
                        variant="outlined"
                        inputProps={{
                          maxLength: 6,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        error={!!errors.pinCode}
                        helperText={errors.pinCode || ''}
                      />
                    </Grid>
                  </Grid>

                  {/* Additional Applicants (Co-applicants) */}
                  <Divider sx={{ my: 3 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Additional Applicants (Co-applicants)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Optional: add more applicants. Leave empty if not needed.
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button variant="outlined" onClick={handleAddApplicant} size="small">
                        Add Applicant
                      </Button>
                      <Button variant="contained" onClick={handleOpenAddApplicantDialog} size="small">
                        Add New Customer
                      </Button>
                    </Box>
                  </Box>

                  {/* Primary applicant auto-filled from selected customer; read-only - only show when customer is selected */}
                  {customerData.customerId && (
                    <Box sx={{ mb: 2, px: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Primary Applicant (auto-filled from customer)
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            value={customerData.name || ''}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Mobile Number"
                            value={customerData.mobile || ''}
                            disabled
                            error={customerData.mobile ? !validateMobile(customerData.mobile) : false}
                            helperText={customerData.mobile && !validateMobile(customerData.mobile) ? 'Mobile number invalid' : ''}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Alternate Number"
                            value={customerData.altMobile || customerData.mobile || ''}
                            disabled
                            error={customerData.altMobile ? !validateMobile(customerData.altMobile) : false}
                            helperText={customerData.altMobile && !validateMobile(customerData.altMobile) ? 'Alternate number invalid' : ''}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Address"
                            value={customerData.address || ''}
                            disabled
                          />
                        </Grid>

                        <Grid item xs={12} sm={3} md={1.6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Relation"
                            value="Self"
                            disabled
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Header row to mimic table layout */}
                  {/* <Grid container spacing={1} sx={{ mb: 1, bgcolor: '#f5f7fb', px: 1, py: 1, borderRadius: 1 }}>
                <Grid item xs={12} sm={3} md={2.4}>
                  <Typography variant="subtitle2">Name</Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={2.4}>
                  <Typography variant="subtitle2">Mobile No.</Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={2.4}>
                  <Typography variant="subtitle2">Alternate No.</Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <Typography variant="subtitle2">Address</Typography>
                </Grid>
                <Grid item xs={12} sm={3} md={1.6}>
                  <Typography variant="subtitle2">Relation</Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={1.2}>
                  <Typography variant="subtitle2">Action</Typography>
                </Grid>
              </Grid> */}

                  {applicants.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No additional applicants added.
                    </Typography>
                  )}

                  {applicants.map((applicant, idx) => {
                    const applicantError = Array.isArray(errors.applicants) ? errors.applicants[idx] || {} : {};
                    return (
                      <Grid container spacing={1} alignItems="center" key={`applicant-${idx}`} sx={{ mb: 2, px: 1, border: '1px solid #e0e0e0', borderRadius: 2, p: 2, position: 'relative' }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Autocomplete
                            fullWidth
                            size="small"
                            loading={loadingCustomersList || loadingMoreCustomers}
                            options={customerDirectory.filter(customer => {
                              // Filter out primary customer
                              const isPrimary = customer.id === customerData.customerId || customer.customerId === customerData.customerId;
                              // Filter out already added applicants (except the current one)
                              const isAlreadyApplicant = applicants.some((a, i) =>
                                i !== idx &&
                                a.customerId &&
                                (a.customerId === customer.id || a.customerId === customer.customerId)
                              );
                              return !isPrimary && !isAlreadyApplicant;
                            })}
                            getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id ||
                              option.customerId === value.customerId ||
                              (option.name === value.name && option.mobile === value.mobile)
                            }
                            inputValue={applicantSearchInputs[idx] || ''}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'input') {
                                handleApplicantSearchChange(idx, value);
                              } else if (reason === 'clear') {
                                handleApplicantSearchChange(idx, '');
                              }
                            }}
                            value={
                              customerDirectory.find(
                                c =>
                                  c.id === applicant.customerId ||
                                  c.customerId === applicant.customerId ||
                                  (applicant.name &&
                                    c.name?.toLowerCase() === applicant.name.toLowerCase() &&
                                    (!c.mobile || !applicant.mobile || c.mobile === applicant.mobile))
                              ) || (applicant.customerId ? {
                                id: applicant.customerId,
                                customerId: applicant.customerId,
                                name: applicant.name,
                                mobile: applicant.mobile,
                                displayLabel: `${applicant.name || ''} — ${applicant.mobile || ''}`
                              } : null)
                            }
                            onChange={(_, value) => handleApplicantSelect(idx, value)}
                            ListboxProps={{
                              onScroll: (event) => {
                                const listboxNode = event.currentTarget;
                                if (
                                  listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                                  customerHasMore &&
                                  !loadingMoreCustomers &&
                                  !loadingCustomersList
                                ) {
                                  const nextPage = customerPage + 1;
                                  setCustomerPage(nextPage);
                                  loadCustomersList(applicantSearchInputs[idx] || '', nextPage, true);
                                }
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Name"
                                placeholder="Enter name"
                                error={!!applicantError.name}
                                helperText={applicantError.name}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingCustomersList || loadingMoreCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Mobile Number"
                            placeholder="Enter mobile number"
                            value={applicant.mobile || ''}
                            onChange={(e) => handleApplicantChange(idx, 'mobile', e.target.value)}
                            error={!!applicantError.mobile}
                            helperText={applicantError.mobile}
                            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Alternate Number"
                            placeholder="Enter alternate number"
                            value={applicant.altMobile || ''}
                            onChange={(e) => handleApplicantChange(idx, 'altMobile', e.target.value)}
                            error={!!applicantError.altMobile}
                            helperText={applicantError.altMobile}
                            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Address"
                            placeholder="Enter address"
                            value={applicant.address || ''}
                            onChange={(e) => handleApplicantChange(idx, 'address', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Relation"
                            value={applicant.relation || ''}
                            onChange={(e) => handleApplicantChange(idx, 'relation', e.target.value)}
                          >
                            <MenuItem value="">Select</MenuItem>
                            {RELATION_OPTIONS.filter(opt => opt !== 'Self').map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={1} display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
                          <Button color="error" onClick={() => handleRemoveApplicant(idx)} size="small">
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    );
                  })}

                  {/* Navigation Buttons */}
                  <Divider sx={{ my: 3 }} />
                  <Box display="flex" justifyContent="space-between" mt={3}>
                    <Button
                      variant="outlined"
                      disabled
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3
                      }}
                    >
                      Previous
                    </Button>
                    {canWritePlotBooking && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={creatingCustomer || updatingCustomer}
                        startIcon={(creatingCustomer || updatingCustomer) ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 4,
                          py: 1.2,
                          boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)'
                        }}
                      >
                        {creatingCustomer ? 'Creating Customer...' : updatingCustomer ? 'Updating Customer...' : 'Next: Booking Information'}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Plot Information Section */}
            {step === 2 && (
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Home sx={{ color: '#1976d2', fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                      Booking Information
                    </Typography>
                  </Box>

                  {/* Booking Type */}
                  <Grid container spacing={3} mb={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth required error={!!errors.bookingType}>
                        <InputLabel>Booking Type</InputLabel>
                        <Select
                          name="bookingType"
                          value={plotData.bookingType}
                          onChange={handlePlotChange}
                          label="Booking Type"
                          disabled={loadingBookingTypes}
                        >
                          {loadingBookingTypes ? (
                            <MenuItem value="" disabled>
                              Loading...
                            </MenuItem>
                          ) : bookingTypes.length > 0 ? (
                            bookingTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              No booking types available
                            </MenuItem>
                          )}
                        </Select>
                        {errors.bookingType && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.bookingType}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        fullWidth
                        options={projectDirectory}
                        getOptionLabel={(opt) => opt?.projectName || opt?.name || ''}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        loading={loadingProjects}
                        inputValue={projectSearchInput}
                        onInputChange={(_, v) => setProjectSearchInput(v)}
                        value={projectDirectory.find(p => p._id === plotData.projectId) || null}
                        onChange={async (_, v) => {
                          const projectName = v?.projectName || v?.name || '';
                          const projectId = v?._id || v?.id || null;
                          const rewardPoints = v?.rewardPoints || 0;
                          const commissionConfig = v?.commissionMethod ? {
                            method: v.commissionMethod,
                            value: v.commissionMethod === 'project' ? v.commission : v.levelCommission,
                            percentage: v.commissionMethod === 'project' ? v.commission : null
                          } : null;

                          // Reset associate commission when project changes
                          setAddAssociateCommission(false);
                          setAssociateCommissionPercent(null);
                          setAssociateCommissionMax(0);
                          setAssociateCommissionError('');

                          const next = { ...plotData, projectName, projectId, rewardPoints, plotNo: '', plotName: '', tower: '', floor: '', unitNo: '', associateCommission: '' };
                          setPlotData(next);
                          setProjectCommission(commissionConfig);

                          if (!projectId) {
                            setPlotsList([]);
                            setBuildingsList([]);
                            setFloorsList([]);
                            setFlatsList([]);
                            setProjectCommission(null);
                            return;
                          }

                          if (plotData.bookingType && projectId) {
                            setLoadingPlots(true);
                            try {
                              const plotsResponse = await getPlotsByProject(
                                projectId,
                                plotData.bookingType,
                                plotData.bookingType,
                                'available'
                              );
                              const plots = Array.isArray(plotsResponse?.inventories)
                                ? plotsResponse.inventories
                                : Array.isArray(plotsResponse)
                                  ? plotsResponse
                                  : [];
                              setPlotsList(plots);

                              // Extract commission information from projectData in the first inventory
                              // All inventories have the same projectData, so we can use the first one
                              if (plots && plots.length > 0 && plots[0]?.projectData) {
                                const projectDataFromInventory = plots[0].projectData;
                                const commissionMethod = projectDataFromInventory.commissionMethod || 'project';
                                const commissionValue = commissionMethod === 'project'
                                  ? (projectDataFromInventory.commission || null)
                                  : (projectDataFromInventory.levelCommission || null);
                                const commissionConfig = {
                                  method: commissionMethod,
                                  value: commissionValue !== null ? String(commissionValue) : null,
                                  percentage: commissionMethod === 'project' && commissionValue !== null
                                    ? String(commissionValue)
                                    : null
                                };
                                setProjectCommission(commissionConfig);
                              }

                              if (plotData.bookingType === 'Apartment' && plots && plots.length > 0) {
                                const buildingMap = new Map();
                                plots.forEach(item => {
                                  const buildingName = item.buildingName || item.building || item.name || item.tower || item.block;
                                  const buildingId = item._id || item.id;
                                  if (buildingName && buildingId && !buildingMap.has(buildingId)) {
                                    buildingMap.set(buildingId, {
                                      name: buildingName,
                                      value: buildingId,
                                      id: buildingId
                                    });
                                  }
                                });
                                setBuildingsList(Array.from(buildingMap.values()));
                              } else {
                                setBuildingsList([]);
                              }
                            } catch (error) {
                              console.error('Failed to load plots:', error);
                              setPlotsList([]);
                              setBuildingsList([]);
                            } finally {
                              setLoadingPlots(false);
                            }
                          } else {
                            setPlotsList([]);
                            setBuildingsList([]);
                            setFloorsList([]);
                            setFlatsList([]);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Project Name"
                            required
                            error={!!errors.projectName}
                            helperText={errors.projectName}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    {/* Reward Points Field - Show only when project is selected */}
                    {/* {plotData.projectId && (
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Reward Points"
                      name="rewardPoints"
                      type="number"
                      value={plotData.rewardPoints}
                      onChange={handlePlotChange}
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                )} */}
                  </Grid>
                  {/* Plot Location Details */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                    Unit Details
                  </Typography>
                  <Grid container spacing={3} mb={4}>
                    {/* <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={!!errors.site}>
                    <InputLabel>Select Site</InputLabel>
                    <Select
                      name="site"
                      value={plotData.site}
                      onChange={handlePlotChange}
                      label="Select Site"
                    >
                      <MenuItem value="Site A">Site A</MenuItem>
                      <MenuItem value="Site B">Site B</MenuItem>
                      <MenuItem value="Site C">Site C</MenuItem>
                    </Select>
                  </FormControl>
                  {errors.site && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.site}
                    </Typography>
                  )}
                </Grid> */}

                    {/* <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={!!errors.sector}>
                    <InputLabel>Select Sector</InputLabel>
                    <Select
                      name="sector"
                      value={plotData.sector}
                      onChange={handlePlotChange}
                      label="Select Sector"
                    >
                      <MenuItem value="Sector 1">Sector 1</MenuItem>
                      <MenuItem value="Sector 2">Sector 2</MenuItem>
                      <MenuItem value="Sector 3">Sector 3</MenuItem>
                    </Select>
                  </FormControl>
                  {errors.sector && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.sector}
                    </Typography>
                  )}
                </Grid> */}

                    {/* <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Block</InputLabel>
                    <Select
                      name="block"
                      value={plotData.block}
                      onChange={handlePlotChange}
                      label="Select Block"
                    >
                      <MenuItem value="Block A">Block A</MenuItem>
                      <MenuItem value="Block B">Block B</MenuItem>
                      <MenuItem value="Block C">Block C</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}

                    {plotData.bookingType === 'Apartment' && (
                      <>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <InputLabel>Building Name</InputLabel>
                            <Select
                              name="tower"
                              value={plotData.tower || ''}
                              onChange={handlePlotChange}
                              label="Building Name"
                              disabled={loadingPlots || buildingsList.length === 0}
                            >
                              {buildingsList.length === 0 ? (
                                <MenuItem value="" disabled>
                                  {loadingPlots ? 'Loading buildings...' : 'No buildings available'}
                                </MenuItem>
                              ) : (
                                buildingsList.map((building) => (
                                  <MenuItem key={building.value} value={building.value}>
                                    {building.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <InputLabel>Floor Number</InputLabel>
                            <Select
                              name="floor"
                              value={plotData.floor || ''}
                              onChange={handlePlotChange}
                              label="Floor Number"
                              disabled={loadingFloors || !plotData.tower || floorsList.length === 0}
                            >
                              {floorsList.length === 0 ? (
                                <MenuItem value="" disabled>
                                  {loadingFloors ? 'Loading floors...' : !plotData.tower ? 'Select building first' : 'No floors available'}
                                </MenuItem>
                              ) : (
                                floorsList.map((floor, index) => {
                                  const floorValue = floor._id || floor.id || floor.floorNo || floor.floorNumber || floor.floor || floor.name || floor.value || String(index);
                                  const floorLabel = floor.floorNo || floor.floorNumber || floor.floor || floor.name || floor.label || floorValue;
                                  return (
                                    <MenuItem key={floorValue} value={floorValue}>
                                      {floorLabel}
                                    </MenuItem>
                                  );
                                })
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        {plotData.tower && plotData.floor && (
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <InputLabel>Flat Number</InputLabel>
                              <Select
                                name="unitNo"
                                value={plotData.unitNo || ''}
                                onChange={handlePlotChange}
                                label="Flat Number"
                                disabled={loadingFlats || !plotData.floor || flatsList.length === 0}
                              >
                                {flatsList.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    {loadingFlats ? 'Loading flats...' : !plotData.floor ? 'Select floor first' : 'No flats available'}
                                  </MenuItem>
                                ) : (
                                  flatsList.map((flat, index) => {
                                    const flatValue = flat._id || flat.id || flat.flatNo || flat.flatNumber || flat.unitNo || flat.name || flat.value || String(index);
                                    const flatLabel = flat.flatNo || flat.flatNumber || flat.unitNo || flat.name || flat.label || flatValue;
                                    return (
                                      <MenuItem key={flatValue} value={flatValue}>
                                        {flatLabel}
                                      </MenuItem>
                                    );
                                  })
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </>
                    )}
                    {(plotData.bookingType === 'Plot' || plotData.bookingType === 'RowHouse' || plotData.bookingType === 'Villa') && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                          fullWidth
                          options={Array.isArray(plotsList) ? plotsList : []}
                          getOptionLabel={(option) => option?.plotName || ''}
                          loading={loadingPlots}
                          value={Array.isArray(plotsList) ? (plotsList.find(p => p?._id === plotData.plotNo) || null) : null}
                          onInputChange={async (_, inputValue) => {
                            // Check if the input value exists in the current plot list
                            const plotExistsInList = plotsList.some(p => p?.plotName?.toLowerCase() === inputValue.toLowerCase());

                            if (!plotExistsInList && inputValue) {
                              // If the plot name doesn't exist in the list, trigger the API fetch
                              setLoadingPlots(true);
                              try {
                                // Trigger the API with search parameter
                                const searchQuery = plotData.bookingType === 'Villa' ? inputValue : inputValue; // use inputValue as the search term
                                const plotsResponse = await getPlotsByProjectSearch(
                                  plotData.projectId,
                                  plotData.bookingType,
                                  plotData.bookingType,
                                  'available',
                                  searchQuery?.toString()?.length > 1 ? searchQuery : '', // Use the input value for the search
                                );

                                const newPlots = Array.isArray(plotsResponse?.inventories)
                                  ? plotsResponse.inventories
                                  : Array.isArray(plotsResponse)
                                    ? plotsResponse
                                    : [];

                                // Update the plot list with the fetched results
                                setPlotsList(newPlots);
                              } catch (error) {
                                console.error('Failed to load plots:', error);
                              } finally {
                                setLoadingPlots(false);
                              }
                            }
                          }}
                          onChange={(_, value) => {
                            if (value) {
                              // If a plot is selected, update the plotData
                              const plotId = value._id || '';
                              const plotName = value.plotName || '';
                              const totalSize = value.totalSize || 0;
                              const width = value.width || 0;
                              const length = value.length || 0;
                              const pricePerSqFt = value.pricePerSqFt || 0;
                              const amount = value.amount || 0;
                              const companyDiscountPercent = value.companyDiscountPercent || 0;
                              const plcPercent = value.plcPercent || 0;
                              const authorityChargesType = value.authorityChargesType || '';
                              const authorityChargesPerSqft = value.authorityChargesPerSqft || 0;
                              const totalAuthorityCharges = value.totalAuthorityCharges || 0;

                              // Update plotData with the selected plot
                              const next = {
                                ...plotData,
                                plotNo: plotId,
                                plotName: plotName,
                                companyDiscountPercent: String(companyDiscountPercent || 0),
                                plcPercent: String(plcPercent || 0),
                                authorityChargesType: authorityChargesType,
                                authorityChargesPerSqft: String(authorityChargesPerSqft),
                                totalAuthorityCharges: String(totalAuthorityCharges),
                                includeAuthorityCharges: true,
                              };

                              if (plotData.bookingType === 'Villa') {
                                const builtUpAreaRaw = value.BuiltUpArea || value.builtUpArea || value.totalSize || 0;
                                const carpetAreaRaw = value.carpetArea || 0;
                                const superAreaRaw = value.totalSize || builtUpAreaRaw || 0;
                                const villaType = value.villaType || value.type || '';

                                const builtUpArea = typeof builtUpAreaRaw === 'string' ? parseFloat(builtUpAreaRaw) || 0 : (builtUpAreaRaw || 0);
                                const carpetArea = typeof carpetAreaRaw === 'string' ? parseFloat(carpetAreaRaw) || 0 : (carpetAreaRaw || 0);
                                const superArea = typeof superAreaRaw === 'string' ? parseFloat(superAreaRaw) || 0 : (superAreaRaw || 0);

                                next.builtUpArea = String(builtUpArea);
                                next.carpetArea = String(carpetArea);
                                next.superArea = String(superArea);
                                next.villaType = String(villaType);
                                next.unitSize = String(superArea || '');
                              } else {
                                next.plotArea = String(totalSize || '');
                                next.plotWidth = String(width || '');
                                next.plotHeight = String(length || '');
                              }

                              if (pricePerSqFt > 0) {
                                next.plotRate = String(pricePerSqFt);
                              }

                              if (amount > 0) {
                                next.actualPlotAmount = String(amount);
                              }

                              // Recalculate amounts (if needed)
                              recalcAmounts(next);

                              setPlotData(next);
                            } else {
                              // If no value is selected, clear the plotData
                              const clearedData = {
                                ...plotData,
                                plotNo: '',
                                plotName: '',
                                plotRate: '',
                                actualPlotAmount: '',
                                companyDiscountPercent: '0',
                                plcPercent: '0',
                                discountPerSqFt: '0',
                                totalDiscountValue: '0',
                                companyDiscount: '0'
                              };

                              if (plotData.bookingType === 'Villa') {
                                clearedData.builtUpArea = '';
                                clearedData.carpetArea = '';
                                clearedData.superArea = '';
                                clearedData.unitSize = '';
                                clearedData.villaType = '';
                              } else {
                                clearedData.plotArea = '';
                                clearedData.plotWidth = '';
                                clearedData.plotHeight = '';
                              }

                              setPlotData(clearedData);
                            }

                            // Clear error for plotNo if any
                            if (errors.plotNo) {
                              setErrors(prev => ({ ...prev, plotNo: '' }));
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                plotData.bookingType === 'RowHouse'
                                  ? 'Row House Number'
                                  : plotData.bookingType === 'Villa'
                                    ? 'Villa Number'
                                    : 'Plot Number'
                              }
                              required
                              error={!!errors.plotNo}
                              helperText={errors.plotNo}
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingPlots ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {(plotData.bookingType === 'Plot' || plotData.bookingType === 'RowHouse') ? (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                        {plotData.bookingType === 'RowHouse' ? 'Row House Dimensions' : 'Plot Dimensions'}
                      </Typography>
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label={plotData.bookingType === 'RowHouse' ? 'Row House L (ft)' : 'Plot L (ft)'}
                            name="plotHeight"
                            type="number"
                            fullWidth
                            value={plotData.plotHeight}
                            onChange={handlePlotChange}
                            variant="outlined"
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label={plotData.bookingType === 'RowHouse' ? 'Row House Width (ft)' : 'Plot Width (ft)'}
                            name="plotWidth"
                            type="number"
                            fullWidth
                            value={plotData.plotWidth}
                            onChange={handlePlotChange}
                            variant="outlined"
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label={plotData.bookingType === 'RowHouse' ? 'Row House Area (sq ft)' : 'Plot Area (sq ft)'}
                            name="plotArea"
                            type="number"
                            fullWidth
                            value={plotData.plotArea}
                            onChange={handlePlotChange}
                            variant="outlined"
                            disabled
                          />
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                        Unit Details
                      </Typography>
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Carpet Area (sq ft)"
                            name="carpetArea"
                            type="number"
                            fullWidth
                            value={plotData.carpetArea}
                            onChange={handlePlotChange}
                            variant="outlined"
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Built-up Area (sq ft)"
                            name="builtUpArea"
                            type="number"
                            fullWidth
                            value={plotData.builtUpArea}
                            onChange={handlePlotChange}
                            variant="outlined"
                            error={!!errors.builtUpArea}
                            helperText={errors.builtUpArea}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Super Area (sq ft)"
                            name="superArea"
                            type="number"
                            fullWidth
                            value={plotData.superArea}
                            onChange={handlePlotChange}
                            variant="outlined"
                            disabled
                          />
                        </Grid>
                        {plotData.bookingType === 'Villa' && (
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Villa Type"
                              name="villaType"
                              fullWidth
                              required
                              value={plotData.villaType}
                              onChange={handlePlotChange}
                              variant="outlined"
                              error={!!errors.villaType}
                              helperText={errors.villaType}
                              disabled
                            />
                          </Grid>
                        )}
                        {plotData.bookingType === 'Apartment' && (
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth required error={!!errors.bedrooms}>
                              <InputLabel>BHK</InputLabel>
                              <Select
                                name="bedrooms"
                                value={plotData.bedrooms}
                                onChange={handlePlotChange}
                                label="BHK"
                              >
                                <MenuItem value="1">1 BHK</MenuItem>
                                <MenuItem value="2">2 BHK</MenuItem>
                                <MenuItem value="3">3 BHK</MenuItem>
                                <MenuItem value="4">4 BHK</MenuItem>
                                <MenuItem value="5">5 BHK</MenuItem>
                              </Select>
                              {errors.bedrooms && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                  {errors.bedrooms}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}

                  {/* Commission Details */}
                  {plotData.projectId && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
                        Commission Details
                      </Typography>
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth disabled>
                            <InputLabel>Commission Method</InputLabel>
                            <Select
                              value={projectCommission?.method || "project"}
                              label="Commission Method"
                              disabled
                            >
                              <MenuItem value="project">Project Commission (%)</MenuItem>
                              <MenuItem value="level">Level Commission (%)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {projectCommission?.method === 'project' && (
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <InputLabel>Commission (%)</InputLabel>
                              <Select
                                value={projectCommission?.percentage || projectCommission?.value || ''}
                                label="Commission (%)"
                                onOpen={() => fetchCommissionOptions()}
                                onChange={(e) => {
                                  const selectedCommission = e.target.value;
                                  setProjectCommission(prev => ({
                                    ...prev,
                                    percentage: selectedCommission,
                                    value: selectedCommission,
                                  }));
                                }}
                              >
                                {loadingCommissionOptions ? (
                                  <MenuItem value="" disabled>Loading...</MenuItem>
                                ) : commissionOptions.length > 0 ? (
                                  commissionOptions.map((option) => (
                                    <MenuItem key={option} value={option}>{option}%</MenuItem>
                                  ))
                                ) : (
                                  <MenuItem value={projectCommission?.percentage || projectCommission?.value || ''}>
                                    {projectCommission?.percentage || projectCommission?.value || ''}%
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}


                        {/* {projectCommission?.method === 'level' && (
    <Grid item xs={12} sm={6} md={4}>
      <FormControl fullWidth disabled>
        <InputLabel>Level Commission</InputLabel>
        <Select
          value={projectCommission?.value || "Level 1"}
          label="Level Commission"
          disabled
        >
          <MenuItem value={projectCommission?.value || "Level 1"}>
            {projectCommission?.value || "Level 1"}
          </MenuItem>
        </Select>
      </FormControl>
    </Grid>
  )} */}
                      </Grid>
                    </>
                  )}
                  <Divider sx={{ my: 2 }} />
                  {/* Reward Points Field - Show only when project is selected */}
                  {plotData.projectId && (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2', mt: 2 }}>
                        Reward Points
                      </Typography>
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            fullWidth
                            label="Reward Points"
                            name="rewardPoints"
                            type="number"
                            value={plotData.rewardPoints}
                            onChange={handlePlotChange}
                            sx={{
                              '& input[type=number]': {
                                MozAppearance: 'textfield',
                              },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />
                  {/* Authority Information */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Authority Information
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={plotData.includeAuthorityCharges !== false}
                          onChange={handlePlotChange}
                          name="includeAuthorityCharges"
                          color="primary"
                        />
                      }
                      label="Include Authority Charges"
                    />
                  </Box>
                  <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Authority Charges Type"
                        value={plotData.authorityChargesType || ''}
                        fullWidth
                        disabled
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Authority Charges (₹/sq ft)"
                        value={plotData.authorityChargesPerSqft || ''}
                        fullWidth
                        disabled
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Total Authority Charges (₹)"
                        value={plotData.totalAuthorityCharges || ''}
                        fullWidth
                        disabled
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />
                  {/* Pricing Information */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                    Pricing Information
                  </Typography>

                  <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Booking Date"
                        name="bookingDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        // Format the bookingDate as YYYY-MM-DD for the value
                        value={bookingDate}
                        onChange={handleBookingDate}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} mb={4}>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Plot Rate (₹/sq ft)"
                        name="plotRate"
                        type="number"
                        fullWidth
                        value={plotData.plotRate}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Discount %"
                        name="companyDiscountPercent"
                        type="number"
                        fullWidth
                        value={plotData.companyDiscountPercent}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Discount per sq ft (₹)"
                        name="discountPerSqFt"
                        type="number"
                        fullWidth
                        value={plotData.discountPerSqFt || '0'}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Total Discount Value (₹)"
                    name="totalDiscountValue"
                    type="number"
                    fullWidth
                    value={plotData.totalDiscountValue}
                    onChange={handlePlotChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    disabled
                  />
                </Grid> */}

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Company Discount"
                        name="companyDiscount"
                        type="number"
                        fullWidth
                        value={plotData.companyDiscount || '0'}
                        onChange={handlePlotChange}
                        variant="outlined"
                        error={!!errors.companyDiscount}
                        helperText={errors.companyDiscount}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="PLC Percentage"
                        name="plcPercent"
                        type="number"
                        fullWidth
                        value={plotData.plcPercent}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Actual Plot Amount (₹)"
                        name="actualPlotAmount"
                        type="number"
                        fullWidth
                        value={plotData.actualPlotAmount}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Net Total Amount (₹)"
                        name="netTotal"
                        type="number"
                        fullWidth
                        value={plotData.netTotal}
                        onChange={handlePlotChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        disabled
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: '#1976d2',
                            fontWeight: 600,
                          },
                        }}
                      />
                    </Grid>

                    {/* <Grid item xs={12} md={4}>
                  <TextField
                    label="Selling Rate (₹/sq ft)"
                    name="sellingRate"
                    type="number"
                    fullWidth
                    value={plotData.sellingRate}
                    onChange={handlePlotChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid> */}

                    {/* <Grid item xs={12} md={4}>
                  <TextField
                    label="Discount Plot Rate (₹/sq ft)"
                    name="discountPlotRate"
                    type="number"
                    fullWidth
                    value={plotData.discountPlotRate}
                    onChange={handlePlotChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid> */}


                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Booking Amount (₹)"
                        name="bookingAmount"
                        type="number"
                        fullWidth
                        value={plotData.bookingAmount || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const netTotal = Number(plotData.netTotal || 0);
                          const bookingValue = Number(value || 0);
                          // Only allow if booking amount is not greater than net total
                          if (bookingValue <= netTotal || value === '') {
                            handlePlotChange(e);
                          }
                        }}
                        onWheel={(e) => e.target.blur()}
                        variant="outlined"
                        inputProps={{
                          min: 0,
                          max: Number(plotData.netTotal || 0),
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        error={Number(plotData.bookingAmount || 0) > Number(plotData.netTotal || 0)}
                        helperText={
                          Number(plotData.bookingAmount || 0) > Number(plotData.netTotal || 0)
                            ? `Booking amount cannot exceed total amount (₹${plotData.netTotal || 0})`
                            : ''
                        }
                        sx={{
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        }}
                      />
                    </Grid>

                    {/* Commission Details */}


                    {/* Payment Mode and Details - Only show if booking amount is entered */}
                    {plotData.bookingAmount && Number(plotData.bookingAmount) > 0 && (
                      <>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl
                            fullWidth
                            required={plotData.bookingAmount && Number(plotData.bookingAmount) > 0}
                            error={!!errors.mode}
                          >
                            <InputLabel>
                              Payment mode {plotData.bookingAmount && Number(plotData.bookingAmount) > 0 ? '' : '(Optional)'}
                            </InputLabel>
                            <Select
                              name="mode"
                              value={plotData.mode}
                              onChange={handlePlotChange}
                              label="Select payment mode"
                            >
                              <MenuItem value="Cash">Cash</MenuItem>
                              <MenuItem value="Cheque">Cheque</MenuItem>
                              <MenuItem value="RTGS/NEFT">RTGS/NEFT</MenuItem>
                              <MenuItem value="UPI">UPI</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                            {errors.mode && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.mode}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Ref. No"
                            name="refNo"
                            fullWidth
                            value={plotData.refNo}
                            onChange={handlePlotChange}
                            variant="outlined"
                            error={!!errors.refNo}
                            helperText={errors.refNo}
                          />
                        </Grid>

                        {renderPaymentModeFields()}
                      </>
                    )}

                    {/* Payment Plan - Always visible as it determines future payment structure */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth required error={!!errors.paymentPlan}>
                        <InputLabel>Payment Plan</InputLabel>
                        <Select
                          name="paymentPlan"
                          value={plotData.paymentPlan || ''}
                          onChange={handlePlotChange}
                          label="Payment Plan"
                          disabled={loadingPaymentPlans}
                        >
                          <MenuItem value="" disabled>
                            Select Payment Plan
                          </MenuItem>
                          {loadingPaymentPlans ? (
                            <MenuItem value="" disabled>
                              Loading...
                            </MenuItem>
                          ) : paymentPlans.length > 0 ? (
                            paymentPlans.map((plan) => (
                              <MenuItem key={plan} value={plan}>
                                {plan}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              No payment plans available
                            </MenuItem>
                          )}
                        </Select>
                        {errors.paymentPlan && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.paymentPlan}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>


                  {/* Payment Information */}
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Payment Information
              </Typography>
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Payment Amount (₹)"
                    name="paymentAmount"
                    type="number"
                    fullWidth
                    value={plotData.paymentAmount}
                    onChange={handlePlotChange}
                    error={!!errors.paymentAmount}
                    helperText={errors.paymentAmount}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Booking Amount (₹)"
                    name="bookingAmount"
                    type="number"
                    fullWidth
                    value={plotData.bookingAmount}
                    onChange={handlePlotChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      name="paymentMode"
                      value={plotData.paymentMode}
                      onChange={handlePlotChange}
                      label="Payment Mode"
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Cheque">Cheque</MenuItem>
                      <MenuItem value="DD">DD</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="NEFT">NEFT</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Payment Date"
                    name="paymentDate"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={plotData.paymentDate}
                    onChange={handlePlotChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Manual Receipt No"
                    name="manualReceiptNo"
                    fullWidth
                    value={plotData.manualReceiptNo}
                    onChange={handlePlotChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="payoutProceed"
                        checked={plotData.payoutProceed}
                        onChange={handlePlotChange}
                      />
                    }
                    label="Payout Proceed"
                    sx={{ 
                      mt: 2,
                      '& .MuiFormControlLabel-label': { fontWeight: 500 }
                    }}
                  />
                </Grid>
              </Grid>

               */}

                  {/* Personnel Information */}
                  {/* <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Personnel Information
              </Typography> */}
                  {/* <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Received By"
                    name="receivedBy"
                    fullWidth
                    value={plotData.receivedBy}
                    onChange={handlePlotChange}
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Given By"
                    name="givenBy"
                    fullWidth
                    value={plotData.givenBy}
                    onChange={handlePlotChange}
                    variant="outlined"
                  />
                </Grid> */}

                  {/* <Grid item xs={12} md={4}>
                  <TextField
                    label="Cash Deposit By"
                    name="cashDepositBy"
                    fullWidth
                    value={plotData.cashDepositBy}
                    onChange={handlePlotChange}
                    variant="outlined"
                  />
                </Grid> */}



                  {/* Associate Commission Question */}
                  {/* <Grid item xs={12} mt={3}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Associate Discount</FormLabel>
                    <RadioGroup
                      row
                      value={addAssociateCommission ? 'yes' : 'no'}
                      onChange={async (_, value) => {
                        if (value === 'yes') {
                          if (!customerData.associateId) {
                            showAlert('Please select an associate before adding associate commission.');
                            setAddAssociateCommission(false);
                            return;
                          }

                          setAddAssociateCommission(true);
                          setAssociateCommissionError('');
                          setAssociateCommissionLoading(true);
                          try {
                            const associateId = customerData.associateId || '';
                            const commissionMethod = projectCommission?.method || 'level';
                            const commissionValue =
                              commissionMethod === 'project'
                                ? (projectCommission?.percentage || projectCommission?.value || '')
                                : '';

                            const flyParams = {
                              associateId,
                              commissionMethod,
                              ...(commissionMethod === 'project' && commissionValue
                                ? { commission: commissionValue }
                                : {}),
                            };

                            const result = await getFlyCommission(flyParams);

                            console.log('flyCommission response body:', result);

                            // Backend may wrap data in different shapes; try common ones
                            const percentRaw =
                              result?.data?.percent ??
                              result?.percent ??
                              (Array.isArray(result?.data) && result.data[0]?.percent);

                            const percent = Number(percentRaw || 0);

                            if (!percent || isNaN(percent)) {
                              const msg =
                                (result && (result.message || result.error)) ||
                                'Failed to fetch commission percent';
                              console.error('Associate commission percent missing/invalid:', msg);
                              setAssociateCommissionError(msg);
                              setAssociateCommissionPercent(null);
                              setAssociateCommissionMax(0);
                              setAssociateCommissionLoading(false);
                            } else {
                              setAssociateCommissionPercent(percent);
                              const base = Number(plotData.netTotal || 0);
                              const maxAmount = Math.round((isNaN(base) ? 0 : base) * (percent / 100));
                              setAssociateCommissionMax(maxAmount);
                              setAssociateCommissionLoading(false);
                            }
                          } catch (err) {
                            console.error('Failed to fetch associate commission percent:', err);
                            setAssociateCommissionError(
                              err instanceof Error ? err.message : 'Failed to fetch commission percent'
                            );
                            setAssociateCommissionLoading(false);
                          }
                        } else {
                          // Reset associate commission and restore net total
                          setAddAssociateCommission(false);
                          setAssociateCommissionPercent(null);
                          setAssociateCommissionMax(0);
                          setAssociateCommissionError('');
                          setPlotData(prev => ({
                            ...prev,
                            associateCommission: '',
                          }));
                        }
                      }}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Grid> */}

                  {/* Associate Commission Amount Input */}
                  {addAssociateCommission && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Associate Discount (₹)"
                        name="associateCommission"
                        type="number"
                        fullWidth
                        value={plotData.associateCommission || ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Allow clearing the field
                          if (raw === '') {
                            setPlotData(prev => ({
                              ...prev,
                              associateCommission: '',
                            }));
                            return;
                          }

                          // Keep only digits and parse as integer
                          const sanitized = String(raw).replace(/\D/g, '');
                          let value = sanitized === '' ? 0 : parseInt(sanitized, 10);
                          if (isNaN(value) || value < 0) {
                            value = 0;
                          }

                          const max = Math.round(associateCommissionMax || 0);
                          if (max > 0 && value > max) {
                            showAlert(`Associate commission amount cannot exceed max allowed ₹${max}.`);
                            value = max;
                          }

                          setPlotData(prev => ({
                            ...prev,
                            associateCommission: String(value),
                          }));
                        }}
                        variant="outlined"
                        disabled={associateCommissionLoading}
                        onWheel={(e) => {
                          // Prevent scroll wheel from changing the value
                          e.target.blur();
                        }}
                        sx={{
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        }}
                        helperText={
                          associateCommissionLoading
                            ? 'Loading commission percent...'
                            : associateCommissionError
                              ? `Error: ${associateCommissionError}`
                              : `Max discount - ₹${Math.round(associateCommissionMax || 0)} `
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} mt={3}>
                    <TextField
                      label="remark"
                      name="narration"
                      fullWidth
                      multiline
                      rows={3}
                      value={plotData.narration}
                      onChange={handlePlotChange}
                      variant="outlined"
                      error={!!errors.narration}
                      helperText={errors.narration}
                    />
                  </Grid>
                  {/* Navigation Buttons */}
                  <Divider sx={{ my: 3 }} />
                  <Box display="flex" flexDirection={{ xs: 'column-reverse', md: 'row' }} justifyContent="space-between" mt={3} gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handlePrevious}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        width: { xs: '100%', md: 'auto' }
                      }}
                    >
                      Previous: Customer Info
                    </Button>
                    {canWritePlotBooking && (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 4,
                          py: 1.2,
                          width: { xs: '100%', md: 'auto' },
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                        }}
                      >
                        {submitting ? 'Booking...' : 'Book'}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </form>

        {/* Add Applicant Customer Dialog */}
        <Dialog
          open={addApplicantDialogOpen}
          onClose={handleCloseAddApplicantDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Add New Customer (Applicant)</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  fullWidth
                  size="small"
                  value={newApplicantForm.name}
                  onChange={(e) => setNewApplicantForm(prev => ({ ...prev, name: e.target.value }))}
                  error={!!newApplicantErrors.name}
                  helperText={newApplicantErrors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mobile Number"
                  fullWidth
                  size="small"
                  value={newApplicantForm.mobile}
                  onChange={(e) => setNewApplicantForm(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  error={!!newApplicantErrors.mobile}
                  helperText={newApplicantErrors.mobile || '10 digits (starts with 6-9)'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Alternate Number"
                  fullWidth
                  size="small"
                  value={newApplicantForm.altMobile}
                  onChange={(e) => setNewApplicantForm(prev => ({ ...prev, altMobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                  error={!!newApplicantErrors.altMobile}
                  helperText={newApplicantErrors.altMobile || 'Optional'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  fullWidth
                  size="small"
                  value={newApplicantForm.email}
                  onChange={(e) => setNewApplicantForm(prev => ({ ...prev, email: e.target.value }))}
                  error={!!newApplicantErrors.email}
                  helperText={newApplicantErrors.email || 'Optional'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Relation"
                  fullWidth
                  size="small"
                  value={newApplicantForm.relation}
                  onChange={(e) => setNewApplicantForm(prev => ({ ...prev, relation: e.target.value }))}
                >
                  <MenuItem value="">Select</MenuItem>
                  {RELATION_OPTIONS.filter(opt => opt !== 'Self').map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddApplicantDialog} disabled={creatingApplicantCustomer}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateApplicantCustomer}
              disabled={creatingApplicantCustomer}
              startIcon={creatingApplicantCustomer ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {creatingApplicantCustomer ? 'Creating...' : 'Create & Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Add City Dialog */}
      <Dialog
        open={addCityDialog.open}
        onClose={handleCloseAddCityDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="City Name"
            type="text"
            fullWidth
            value={addCityDialog.cityName}
            onChange={(e) => setAddCityDialog(prev => ({ ...prev, cityName: e.target.value }))}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddCity();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCityDialog}>Cancel</Button>
          <Button onClick={handleAddCity} variant="contained">Add City</Button>
        </DialogActions>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BookPlot;

